import { locationHref, writeLocation } from "../utils/appLocation";
import PropTypes from 'prop-types';
import PlanningPassengersModal from '../components/PlanningPassengersModal';
import Icon from "../components/Icon";
import { Toast, PageTitle, Card, Btn, Input, Select, Spinner, TrechoToggle, RegiaoBadge, Pagination, Modal, ConfirmModal } from "../components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../api";
import { REGIAO_COLORS } from "../constants";
import PlanningDispatchModal from "../components/PlanningDispatchModal";
import { sendPlanningVans, retryPlanningVan, planningDispatchMessage } from "../utils/routeFlow";
import PlanningWorkspace from '../components/PlanningWorkspace';
import PlanningBoard from '../components/PlanningBoard';
import PlanningRegions from '../components/PlanningRegions';
import { packRegionsByCapacity, typicalCapacity } from '../utils/vanBoard';
import { previewPassengerTransfer } from '../utils/passengerDistribution';
import PlanningVanCorrection from '../components/PlanningVanCorrection';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import { groupsFingerprint, regionsFingerprint, numberPlanningGroups, preserveReview, vanReady, planningDispatchIndexes } from '../utils/planningWorkspace';
import { useTenant } from "../context/TenantContext";
import { recordUiError } from "../observability";
import { useTenantGeography } from "../hooks/useTenantGeography";
import { formatTrechoCurto, formatTrechoLabel, getDestinoLabel } from "../utils/tenantLabels";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { buildRouteRegistrationBody, dispatchRouteGroups, finalizeRouteDispatch, formatPhoneValidationFalhas, formatRouteDispatchError, formatSgtConferencias } from "../utils/routeFlow";
import {
  buildProgramRegionGroupsFromPeople,
  splitRegionGroupsIntoVanGroups,
  attachProgramToVanGroup,
  attachProgramsToVanGroups,
} from "../utils/programPlanning";

const getTodayDate = () => new Date().toISOString().split("T")[0];
// Quem prefere a montagem detalhada continua nela nas próximas montagens deste navegador.
const DETAILED_WORKSPACE_KEY = "rotaflex_montagem_detalhada";
const readDetailedPreference = () => { try { return localStorage.getItem(DETAILED_WORKSPACE_KEY) === "1"; } catch { return false; } };
const writeDetailedPreference = (value) => {
  try { if (value) localStorage.setItem(DETAILED_WORKSPACE_KEY, "1"); else localStorage.removeItem(DETAILED_WORKSPACE_KEY); } catch { /* preferência só desta sessão */ }
};
const SGT_MOTIVO_MENSAGENS = {
  SEM_SNAPSHOT: "Ainda não existe nenhuma busca automática do SGT registrada.",
  SNAPSHOT_ANTIGO: "A última busca automática do SGT está desatualizada.",
  DATA_FORA_DO_PERIODO: "A data da rota está fora do período coberto pela última busca do SGT.",
  PEDIDOS_NAO_ENCONTRADOS: "Pedido(s) da van não encontrados na última busca do SGT.",
};
const formatSgtSnapshotLine = (snapshot) => {
  if (!snapshot?.criado_em) return null;
  const quando = new Date(snapshot.criado_em).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const idade = snapshot.idade_horas != null ? ` — ${snapshot.idade_horas}h atrás` : "";
  return `Busca do SGT de ${quando}${idade}.`;
};
const matchesEmpresaDestino = (person, empresaDestinoId) => {
  if (!empresaDestinoId) return true;
  return String(person?.empresa_destino_id || "") === String(empresaDestinoId);
};
// "às 02:00" ou, com pedidos de horários diferentes na mesma viagem, "das 02:00 às 02:25".
const planningTimeRange = (programs = [], fallback = "") => {
  const times = [...new Set(programs.map((program) => String(program.horario || "").slice(0, 5)).filter(Boolean))].sort();
  if (times.length > 1) return `das ${times[0]} às ${times[times.length - 1]}`;
  return `às ${times[0] || String(fallback || "").slice(0, 5)}`;
};
const createGroupId = (prefix = "grupo") => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getPersonIdentity = (person) => {
  if (person?.programacao_passageiro_id != null) return `pg-passageiro:${person.programacao_passageiro_id}`;
  if (person?.id != null) return `id:${person.id}`;
  return [
    person?.nome || "",
    person?.telefone || "",
    person?.rua || "",
    person?.numero || "",
  ].join("|");
};
const normalizeDraftGroups = (groups = []) => (
  groups.map((group) => ({
    regiao: group?.regiao || "",
    pessoas: Array.isArray(group?.pessoas)
      ? group.pessoas.map((person) => ({
        ...person,
        ...(person?.id_pedido_origem ? { id_pedido_origem: person.id_pedido_origem } : {}),
      }))
      : [],
    veiculo: group?.veiculo || null,
    motorista: group?.motorista || null,
    trecho: group?.trecho || null,
    programacao_responsavel_id: group?.programacao_responsavel_id || null,
    programacao_codigo_responsavel: group?.programacao_codigo_responsavel || null,
    planejamento_grupo_id: group?.planejamento_grupo_id || null,
    planejamento_van_numero: group?.planejamento_van_numero || null,
    conferida: Boolean(group?.conferida), conferida_em: group?.conferida_em || null, conferida_por: group?.conferida_por || null,
    conferencia_solicitada: Boolean(group?.conferencia_solicitada), data: group?.data || null, horario: group?.horario || null,
  }))
);
const buildDraftRegionGroups = (groups = []) => {
  const groupedMap = new Map();

  normalizeDraftGroups(groups).forEach((group) => {
    const key = group.regiao || "Sem região";
    const current = groupedMap.get(key) || {
      id: createGroupId("regiao"),
      regiao: key,
      pessoas: [],
      qtdVeiculos: 0,
      origens: [key],
      subgrupos: [],
    };

    current.pessoas.push(...group.pessoas);
    current.qtdVeiculos += 1;
    current.subgrupos.push({
      ...group,
      regiao: group.regiao || key,
      pessoas: Array.isArray(group.pessoas) ? group.pessoas : [],
      veiculo: group.veiculo || null,
      motorista: group.motorista || null,
      trecho: group.trecho || null,
      programacao_responsavel_id: group.programacao_responsavel_id || null,
      programacao_codigo_responsavel: group.programacao_codigo_responsavel || null,
      planejamento_grupo_id: group.planejamento_grupo_id || null,
    });
    groupedMap.set(key, current);
  });

  return [...groupedMap.values()].sort((a, b) => b.pessoas.length - a.pessoas.length);
};

const buildProgramGroupRegionLabel = (people = []) => {
  const localities = [...new Set(
    people.map((person) => String(person?.regiao || person?.bairro || person?.cidade || "").trim()).filter(Boolean)
  )];
  return localities.join(" + ") || "Sem região";
};

const withProgramGroupRegionLabels = (groups = []) => (
  groups.map((group) => ({
    ...group,
    regiao: buildProgramGroupRegionLabel(group.pessoas),
  }))
);

export default function MontarRotaPage({
  pessoas,
  motoristas,
  veiculos,
  draftRoute,
  onClearDraftRoute,
  programPlanningContext,
  onClearProgramPlanning,
  embedded = false,
  onBack,
  onPlanningComplete,
  onEditProgram,
  onUndoPlanning,
}) {
  const { config } = useTenant();
  const { regiao: REGIAO, cidadeTemRegiao, ready:geographyReady } = useTenantGeography(programPlanningContext?.programs?.[0]?.cliente_id || draftRoute?.cliente_id || null);
  const [search, setSearch] = useState("");
  const [regiaoFilter, setRegiaoFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;
  const [trecho, setTrecho] = useState("ida");
  const [step, setStep] = useState(1);
  const [gruposRegiao, setGruposRegiao] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [horario, setHorario] = useState("00:00");
  const [data, setData] = useState(() => getTodayDate());
  const [idPedido, setIdPedido] = useState("");
  const [isAgruparModalOpen, setIsAgruparModalOpen] = useState(false);
  const [gruposSelecionadosParaAgrupar, setGruposSelecionadosParaAgrupar] = useState([]);
  const [nomeAgrupamento, setNomeAgrupamento] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState("");
  const [isTestRoute, setIsTestRoute] = useState(false);
  const [routeOutcome, setRouteOutcome] = useState(null);
  const [pendingAction, setPendingAction] = useState("");
  const [phoneValidationDialog, setPhoneValidationDialog] = useState(null);
  const [sgtValidationDialog, setSgtValidationDialog] = useState(null);
  const [sgtOverrideMotivo, setSgtOverrideMotivo] = useState("");
  const [editingDraft, setEditingDraft] = useState(null);
  const [editConflict, setEditConflict] = useState("");
  const [draftSnapshotPessoas, setDraftSnapshotPessoas] = useState([]);
  const [programRoute, setProgramRoute] = useState(null);
  const [passengerEditor, setPassengerEditor] = useState(null);
  const [passengerEditGate, setPassengerEditGate] = useState(null);
  const [planningPrograms, setPlanningPrograms] = useState([]);
  const initializedPlanningContext = useRef(null);
  const [distributionPending, setDistributionPending] = useState(false);
  const [markedGroups, setMarkedGroups] = useState(() => new Set());
  const [planningReview, setPlanningReview] = useState(null);
  const [lastPlanningDispatchId, setLastPlanningDispatchId] = useState(null);
  const [leaveAction, setLeaveAction] = useState(null);
  const [planningProgress, setPlanningProgress] = useState({});
  const [correctionRoute, setCorrectionRoute] = useState(null);
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("success");
  const [empresasDestino, setEmpresasDestino] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(true);
  const [empresaDestinoId, setEmpresaDestinoId] = useState("");
  // Quadro de vans: a montagem detalhada continua disponível para juntar,
  // dividir e trocar a PG de referência.
  const [detailedWorkspace, setDetailedWorkspaceState] = useState(readDetailedPreference);
  const setDetailedWorkspace = (value) => { writeDetailedPreference(value); setDetailedWorkspaceState(value); };
  const [boardTouched, setBoardTouched] = useState(false);
  const [autosave, setAutosave] = useState({ status: 'idle' });
  const [autosaveTick, setAutosaveTick] = useState(0);
  const autosaveBusy = useRef(false);
  const showError = (msg) => {
    recordUiError({ message: msg, page: "pedidos_montar_rota", source: "toast" });
    setToastType("error");
    setToast(msg);
  };

  useEffect(() => {
    if (!toast || toastType === "error") return undefined;
    const timer = setTimeout(() => setToast(""), 5000);
    return () => clearTimeout(timer);
  }, [toast, toastType]);

  useEffect(() => {
    apiFetch("/empresas-destino")
      .then((rows) => setEmpresasDestino(Array.isArray(rows) ? rows : []))
      .catch(() => setEmpresasDestino([]))
      .finally(() => setEmpresasLoading(false));
  }, []);

  const selectedEmpresa = useMemo(
    () => empresasDestino.find((empresa) => String(empresa.id) === String(empresaDestinoId)) || null,
    [empresasDestino, empresaDestinoId]
  );
  const routeConfig = useMemo(() => ({
    ...config,
    destino_label: selectedEmpresa?.nome || config?.destino_label,
    destino_endereco: selectedEmpresa?.endereco || config?.destino_endereco,
  }), [config, selectedEmpresa]);
  const isRouteComplete = Boolean(routeOutcome && step === 4);
  const needsEmpresaSelection = !isRouteComplete && !empresasLoading && geographyReady && empresasDestino.length > 1 && !empresaDestinoId;
  const canShowRouteBuilder = !empresasLoading && geographyReady && empresasDestino.length > 0 && (Boolean(empresaDestinoId) || isRouteComplete);
  const pessoasDoTenant = pessoas.filter((person) => matchesEmpresaDestino(person, empresaDestinoId));
  const motoristasDisponiveis = selectedEmpresa?.cliente_id
    ? motoristas.filter((item) => String(item.cliente_id || "") === String(selectedEmpresa.cliente_id))
    : motoristas;
  const veiculosDisponiveis = selectedEmpresa?.cliente_id
    ? veiculos.filter((item) => String(item.cliente_id || "") === String(selectedEmpresa.cliente_id))
    : veiculos;

  const isMobile = useMediaQuery("(max-width: 767px)");
  const currentPeopleIds = new Set(pessoasDoTenant.map((person) => getPersonIdentity(person)));
  const draftOrigemByPersonId = useMemo(() => {
    const map = new Map();
    for (const person of draftSnapshotPessoas) {
      if (person?.id != null && person?.id_pedido_origem) {
        map.set(Number(person.id), person.id_pedido_origem);
      }
    }
    return map;
  }, [draftSnapshotPessoas]);
  const pessoasComOrigem = pessoasDoTenant.map((person) => {
    const origem = draftOrigemByPersonId.get(Number(person.id));
    return origem ? { ...person, id_pedido_origem: origem } : person;
  });
  const availablePessoas = [
    ...pessoasComOrigem,
    ...draftSnapshotPessoas
      .filter((person) => !currentPeopleIds.has(getPersonIdentity(person)))
      .map((person) => ({ ...person, __draftOnly: true })),
  ];

  const filtered = availablePessoas.filter(p => {
    const s = search.toLowerCase();
    return (!s || p.nome.toLowerCase().includes(s) || p.bairro?.toLowerCase().includes(s) || p.rua?.toLowerCase().includes(s))
      && (!regiaoFilter || (cidadeTemRegiao(p.cidade) && p.regiao === regiaoFilter));
  });

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };
  const handleRegiaoFilter = (e) => { setRegiaoFilter(e.target.value); setPage(1); };

  // Seleciona/deseleciona todos os itens filtrados (não só a página atual)
  const toggleAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(p => p.id)));
  const toggleOne = (id) => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); setSelected(next); };
  const isPlanningPrograms = Boolean(programPlanningContext?.programacaoIds?.length);
  const planningPassengerCandidates = [...new Map([
    ...planningPrograms.flatMap(program => (program.passageiros || []).map(person => ({ ...person, id: person.funcionario_id }))),
    ...pessoasDoTenant,
  ].map(person => [Number(person.id), person])).values()].filter(person => person.ativo !== false);

  const selectedPessoas = isPlanningPrograms
    ? (grupos.length ? grupos.flatMap((group) => group.pessoas) : programRoute ? (programRoute.payload?.grupos || []).flatMap((group) => group.pessoas) : (programPlanningContext.pessoas || []))
    : availablePessoas.filter(p => selected.has(p.id));
  const idPedidoLimpo = idPedido.trim();
  const canAdvanceStep2 = selected.size > 0;
  const canConfirmarAgrupamento = gruposSelecionadosParaAgrupar.length >= 2;
  const isEditingDraft = Boolean(editingDraft?.id);
  const hasRegionalPeople = availablePessoas.some((p) => cidadeTemRegiao(p.cidade));
  const selectedUsesRegions = selectedPessoas.some((p) => cidadeTemRegiao(p.cidade));
  const groupingLabel = selectedUsesRegions ? "Região" : "Cidade";
  const groupingPluralLabel = selectedUsesRegions ? "Regiões" : "Cidades";
  const baselineGroups = withProgramGroupRegionLabels(programRoute?.payload?.grupos || []);
  const hasUnsavedPlanningChanges = isPlanningPrograms && (programRoute
    ? step === 2 ? regionsFingerprint(gruposRegiao) !== regionsFingerprint(buildDraftRegionGroups(baselineGroups))
      : groupsFingerprint(grupos) !== groupsFingerprint(baselineGroups)
    : grupos.length > 0 || gruposRegiao.length > 0);
  const requestLeave = useCallback((action, afterSave = action) => {
    if (distributionPending) {
      setToastType('error'); setToast('Salve a distribuição ou descarte a prévia antes de sair.');
      return;
    }
    if (hasUnsavedPlanningChanges) setLeaveAction({ run: action, afterSave }); else action();
  }, [hasUnsavedPlanningChanges, distributionPending]);
  const planningDirtyRef = useRef(hasUnsavedPlanningChanges);
  planningDirtyRef.current = hasUnsavedPlanningChanges || distributionPending;
  useUnsavedChanges(hasUnsavedPlanningChanges || distributionPending, requestLeave);
  const hasProcessingVans = (programRoute?.vans_enviadas || []).some(van => ['enfileirado_meta','enfileirando_meta'].includes(van.envio_status)
    && !['failed','dead_letter'].includes(van.notification_status) && van.status !== 'enviada');
  useEffect(() => {
    if (!programRoute?.id || !hasProcessingVans || loading) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const current = await apiFetch(`/rotas/${programRoute.id}`);
        if (cancelled) return;
        if (planningDirtyRef.current) setProgramRoute(previous => ({ ...previous, vans_enviadas: current.vans_enviadas }));
        else { if (current.edit_version !== programRoute.edit_version) setMarkedGroups(new Set()); setProgramRoute(current); setGrupos(withProgramGroupRegionLabels(current.payload.grupos)); }
      } catch { /* A atualização manual permanece disponível. */ }
    }, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [programRoute?.id, programRoute?.edit_version, hasProcessingVans, loading, hasUnsavedPlanningChanges]);

  const resetForm = (clearDraftContext = true, preserveEmpresa = false) => {
    setStep(1);
    setSelected(new Set());
    setGruposRegiao([]);
    setGrupos([]);
    setRoutes([]);
    setTrecho("ida");
    setHorario("00:00");
    setData(getTodayDate());
    setIdPedido("");
    setEditConflict("");
    setSearch("");
    setRegiaoFilter("");
    setPage(1);
    setIsAgruparModalOpen(false);
    setGruposSelecionadosParaAgrupar([]);
    setNomeAgrupamento("");
    setIsTestRoute(false);
    setRouteOutcome(null);
    setConfirmAction("");
    setPendingAction("");
    setIsConfirmModalOpen(false);
    setEditingDraft(null);
    setProgramRoute(null);
    setDraftSnapshotPessoas([]);
    if (!preserveEmpresa && empresasDestino.length !== 1) {
      setEmpresaDestinoId("");
    }
    if (clearDraftContext) {
      onClearDraftRoute?.();
      onClearProgramPlanning?.();
    }
  };

  useEffect(() => {
    if (!programPlanningContext?.programacaoIds?.length) { initializedPlanningContext.current = null; return; }
    if (!geographyReady || initializedPlanningContext.current === programPlanningContext) return;
    initializedPlanningContext.current = programPlanningContext;

    const empresaId = programPlanningContext.empresaDestinoId || "";
    if (empresaId) setEmpresaDestinoId(String(empresaId));
    setEditingDraft(null);
    setProgramRoute(null);
    setPassengerEditor(null);
    setPlanningPrograms(programPlanningContext.programs || []);
    setDraftSnapshotPessoas(programPlanningContext.pessoas || []);
    setSelected(new Set((programPlanningContext.pessoas || []).map((person) => person.id).filter(Boolean)));
    setTrecho(programPlanningContext.trecho || "volta");
    setData(programPlanningContext.data || getTodayDate());
    setHorario(programPlanningContext.horario || "00:00");
    setIdPedido(programPlanningContext.idPedido || "");
    setMarkedGroups(new Set());
    setLastPlanningDispatchId(null);
    setPlanningProgress({});
    setDetailedWorkspaceState(readDetailedPreference());
    setBoardTouched(false);
    setAutosave({ status: 'idle' });
    if (programPlanningContext.savedRoute) {
      const saved = programPlanningContext.savedRoute;
      setProgramRoute(saved);
      const requestedGroup = (saved.payload?.grupos || []).find(group => group.planejamento_grupo_id === programPlanningContext.editPassengerGroupId);
      if (requestedGroup) setPassengerEditor({ group: requestedGroup, version: saved.edit_version });
      setGrupos(withProgramGroupRegionLabels(saved.payload?.grupos || []));
      setGruposRegiao(buildDraftRegionGroups(saved.payload?.grupos || []));
      setStep(3);
    } else {
      const usesRegions = (programPlanningContext.pessoas || []).some((person) => cidadeTemRegiao(person.cidade));
      const regions = buildProgramRegionGroupsFromPeople(programPlanningContext.pessoas || [], { usesRegions, cityHasRegions:cidadeTemRegiao });
      const capacities = veiculos.filter(vehicle => !programPlanningContext.programs?.[0]?.cliente_id || String(vehicle.cliente_id) === String(programPlanningContext.programs[0].cliente_id)).map(vehicle => Number(vehicle.capacidade)).filter(capacity => capacity > 0);
      if (readDetailedPreference()) {
        // Montagem detalhada: começa pela distribuição por região, como antes.
        const capacity = capacities.length ? Math.max(...capacities) : null;
        setGruposRegiao(regions.map(region => ({ ...region, qtdVeiculos: capacity ? Math.ceil(region.pessoas.length / capacity) : 1 })));
        setGrupos([]);
        setStep(2);
      } else {
        // A sugestão já respeita a lotação e abre direto no quadro de vans.
        const packed = packRegionsByCapacity(regions, typicalCapacity(capacities));
        setGruposRegiao(packed);
        try {
          setGrupos(vanGroupsFromRegions(packed, programPlanningContext.programs || [], null));
          setStep(3);
        } catch {
          setGrupos([]);
          setStep(2);
        }
      }
    }
    setIsTestRoute(Boolean(programPlanningContext.isTeste));
    setRouteOutcome(null);
    setRoutes([]);
    setPendingAction("");
    setIsConfirmModalOpen(false);
  }, [programPlanningContext, cidadeTemRegiao, veiculos, geographyReady]);

  useEffect(() => {
    if (empresasLoading || empresaDestinoId || editingDraft?.id) return;
    if (empresasDestino.length === 1) {
      setEmpresaDestinoId(String(empresasDestino[0].id));
    }
  }, [empresasLoading, empresasDestino, empresaDestinoId, editingDraft?.id]);

  useEffect(() => {
    if (!draftRoute?.id) {
      if (editingDraft?.id && step < 4) {
        resetForm(false, true);
      }
      return;
    }

    if (editingDraft?.id === draftRoute.id) {
      return;
    }

    const payloadGroups = normalizeDraftGroups(draftRoute?.payload?.grupos);
    const payload = draftRoute?.payload && typeof draftRoute.payload === "object" ? draftRoute.payload : {};
    const draftEmpresaId = payload.empresa_destino_id
      || payloadGroups.flatMap((group) => group.pessoas || []).find((person) => person?.empresa_destino_id)?.empresa_destino_id
      || "";
    if (draftEmpresaId) {
      setEmpresaDestinoId(String(draftEmpresaId));
    }
    const selectedIds = payloadGroups.flatMap((group) => (
      Array.isArray(group.pessoas) ? group.pessoas.map((person) => person?.id).filter(Boolean) : []
    ));

    setEditingDraft({
      id: draftRoute.id,
      edit_version: draftRoute.edit_version,
      id_pedido: draftRoute.id_pedido || payload.id_pedido || "",
      is_teste: Boolean(draftRoute.is_teste),
      hadDispatch: Boolean(draftRoute.tem_envio_iniciado) || draftRoute.status === "enviada",
    });
    setEditConflict("");
    setSearch("");
    setRegiaoFilter("");
    setSelected(new Set(selectedIds));
    setDraftSnapshotPessoas(payloadGroups.flatMap((group) => group.pessoas || []));
    setPage(1);
    setTrecho(draftRoute.trecho || payload.trecho || "ida");
    setStep(payloadGroups.length > 0 ? 3 : 1);
    setGruposRegiao(buildDraftRegionGroups(payloadGroups));
    setGrupos(payloadGroups);
    setLoading(false);
    setRoutes([]);
    setHorario(draftRoute.horario || payload.horario || "00:00");
    const rawDate = draftRoute.data || payload.data || null;
    setData(rawDate ? String(rawDate).substring(0, 10) : getTodayDate());
    setIdPedido(draftRoute.id_pedido || payload.id_pedido || "");
    setIsAgruparModalOpen(false);
    setGruposSelecionadosParaAgrupar([]);
    setNomeAgrupamento("");
    setIsConfirmModalOpen(false);
    setConfirmAction("");
    setIsTestRoute(Boolean(draftRoute.is_teste));
    setRouteOutcome(null);
    setPendingAction("");
  }, [draftRoute, editingDraft?.id, step]);

  const persistDraft = async (groupsOverride = null) => {
    if (isPlanningPrograms) {
      const result = await apiFetch(programRoute?.id ? `/rotas/${programRoute.id}/planejamento` : "/programacoes/planejar", {
        method: programRoute?.id ? "PATCH" : "POST",
        body: JSON.stringify({
          programacao_ids: programPlanningContext.programacaoIds,
          grupos: groupsOverride || grupos,
          cliente_id: selectedEmpresa?.cliente_id || null,
          replanejar_rota_id: programPlanningContext.replanejarRotaId || programRoute?.id || null,
          edit_version: programRoute?.edit_version,
        }),
      });
      const saved = result.rota || result;
      setProgramRoute(saved);
      setGrupos(withProgramGroupRegionLabels(saved.payload.grupos));
      if (step === 2) setGruposRegiao(buildDraftRegionGroups(withProgramGroupRegionLabels(saved.payload.grupos)));
      return saved;
    }
    const body = buildRouteRegistrationBody({
      trecho,
      horario,
      data,
      idPedido: idPedidoLimpo,
      groups: grupos,
      status: "rascunho",
      isTestRoute,
      empresaDestinoId: empresaDestinoId || null,
      clienteId: selectedEmpresa?.cliente_id || null,
      rotasOrigemIds: [],
    });

    if (editingDraft?.id) {
      const updated = await apiFetch(`/rotas/${editingDraft.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...body,
          edit_version: editingDraft.edit_version,
          // O modal de revisão deixa explícito que a mensagem antiga não pode
          // ser recolhida. O backend exige esta confirmação somente quando já
          // existe efeito externo aceito pela Meta.
          confirm_external_effects: Boolean(editingDraft.hadDispatch),
        }),
      }).catch(error => {
        if (["ROUTE_EDIT_CONFLICT", "ROUTE_VERSION_REQUIRED"].includes(error.code)) {
          setEditConflict(error.message);
        }
        throw error;
      });
      setEditConflict("");
      setEditingDraft({
        id: updated.id,
        edit_version: updated.edit_version,
        id_pedido: updated.id_pedido || "",
        is_teste: Boolean(updated.is_teste),
      });
      return updated;
    }

    const created = await apiFetch("/rotas/registrar", {
      method: "POST",
      body: JSON.stringify(body),
    });
    setEditingDraft({
      id: created.id,
      edit_version: created.edit_version,
      id_pedido: created.id_pedido || "",
      is_teste: Boolean(created.is_teste),
    });
    return created;
  };

  const handleAvancarStep2 = () => {
    if (!selected.size) { showError("Selecione ao menos um passageiro para continuar."); return; }
    if (!idPedidoLimpo) {
      showError("Informe o código do pedido para continuar.");
      return;
    }

    const mapa = {};
    selectedPessoas.forEach(p => {
      const chave = cidadeTemRegiao(p.cidade)
        ? (p.regiao || `${p.cidade || "Cidade"} - Sem região`)
        : (p.cidade || "Sem cidade");
      if (!mapa[chave]) mapa[chave] = [];
      mapa[chave].push(p);
    });
    const lista = Object.entries(mapa)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([regiao, pess]) => ({
        id: createGroupId("regiao"),
        regiao,
        pessoas: pess,
        qtdVeiculos: 1,
        origens: [regiao],
        subgrupos: null,
      }));
    setGruposRegiao(lista);
    setStep(2);
  };

  const toggleGrupoParaAgrupar = (grupoId) => {
    setGruposSelecionadosParaAgrupar(prev =>
      prev.includes(grupoId) ? prev.filter(id => id !== grupoId) : [...prev, grupoId]
    );
  };

  const abrirModalAgrupar = () => {
    setGruposSelecionadosParaAgrupar([]);
    setNomeAgrupamento("");
    setIsAgruparModalOpen(true);
  };

  const fecharModalAgrupar = () => {
    setIsAgruparModalOpen(false);
    setGruposSelecionadosParaAgrupar([]);
    setNomeAgrupamento("");
  };

  const confirmarAgrupamentoRegioes = () => {
    if (!canConfirmarAgrupamento) {
      showError("Selecione pelo menos duas regiões para agrupar.");
      return;
    }

    const gruposSelecionados = gruposRegiao.filter(gr => gruposSelecionadosParaAgrupar.includes(gr.id));
    const gruposRestantes = gruposRegiao.filter(gr => !gruposSelecionadosParaAgrupar.includes(gr.id));
    const origens = [...new Set(gruposSelecionados.flatMap(gr => gr.origens || [gr.regiao]))];
    const pessoasAgrupadas = gruposSelecionados.flatMap(gr => gr.pessoas);
    const nomeFinal = nomeAgrupamento.trim() || origens.join(" + ");

    const grupoAgrupado = {
      id: createGroupId("agrupado"),
      regiao: nomeFinal,
      pessoas: pessoasAgrupadas,
      qtdVeiculos: 1,
      origens,
      subgrupos: gruposSelecionados.map(gr => ({ ...gr })),
    };

    setGruposRegiao(
      [...gruposRestantes, grupoAgrupado]
        .sort((a, b) => b.pessoas.length - a.pessoas.length)
    );
    fecharModalAgrupar();
  };

  const desagruparRegioes = (grupoId) => {
    setGruposRegiao(prev => {
      const grupoAgrupado = prev.find(gr => gr.id === grupoId);
      if (!grupoAgrupado?.subgrupos?.length) return prev;

      const gruposRestantes = prev.filter(gr => gr.id !== grupoId);
      return [...gruposRestantes, ...grupoAgrupado.subgrupos]
        .sort((a, b) => b.pessoas.length - a.pessoas.length);
    });
  };

  function vanGroupsFromRegions(regions, programs, route) {
    const people = regions.reduce((sum, group) => sum + group.pessoas.length, 0);
    const totalVeiculos = regions.reduce((sum, group) => sum + (Number(group.qtdVeiculos) || 1), 0);
    if (totalVeiculos < 1 || totalVeiculos > people) {
      throw new Error("Defina ao menos uma van e mantenha pelo menos um passageiro em cada van.");
    }
    const vanGroups = splitRegionGroupsIntoVanGroups(regions, getPersonIdentity);
    const withPrograms = attachProgramsToVanGroups(vanGroups, programs, {
      reservedMotoristaIds: (route?.vans_enviadas || []).map((van) => Number(van.motorista?.id)),
      reservedVeiculoIds: (route?.vans_enviadas || []).map((van) => Number(van.veiculo?.id)),
    });
    return numberPlanningGroups(withProgramGroupRegionLabels(withPrograms), route);
  }
  const buildPendingVanGroups = () => vanGroupsFromRegions(gruposRegiao, programPlanningContext.programs || [], programRoute);
  const handleAvancarStep3 = () => {
    if (isPlanningPrograms) {
      try {
        setGrupos(buildPendingVanGroups());
        setMarkedGroups(new Set());
        setStep(3);
      } catch (error) {
        showError(error?.message || "Não foi possível montar as vans por pedido.");
      }
      return;
    }

    const novosGrupos = [];
    gruposRegiao.forEach(gr => {
      const n = gr.qtdVeiculos;
      const subgruposOriginais = Array.isArray(gr.subgrupos) ? gr.subgrupos : [];

      if (subgruposOriginais.length > 0 && subgruposOriginais.length === n) {
        const pessoasPorSubgrupo = subgruposOriginais.map(() => []);
        const pessoaParaSubgrupo = new Map();

        subgruposOriginais.forEach((subgrupo, subIndex) => {
          (subgrupo.pessoas || []).forEach((person) => {
            pessoaParaSubgrupo.set(getPersonIdentity(person), subIndex);
          });
        });

        const pessoasSemHistorico = [];
        gr.pessoas.forEach((person) => {
          const originalIndex = pessoaParaSubgrupo.get(getPersonIdentity(person));
          if (Number.isInteger(originalIndex) && originalIndex >= 0 && originalIndex < pessoasPorSubgrupo.length) {
            pessoasPorSubgrupo[originalIndex].push(person);
          } else {
            pessoasSemHistorico.push(person);
          }
        });

        pessoasSemHistorico.forEach((person) => {
          let targetIndex = 0;
          for (let i = 1; i < pessoasPorSubgrupo.length; i += 1) {
            if (pessoasPorSubgrupo[i].length < pessoasPorSubgrupo[targetIndex].length) {
              targetIndex = i;
            }
          }
          pessoasPorSubgrupo[targetIndex].push(person);
        });

        pessoasPorSubgrupo.forEach((pessoasDoGrupo, subIndex) => {
          if (!pessoasDoGrupo.length) return;
          const original = subgruposOriginais[subIndex] || {};
          novosGrupos.push({
            regiao: gr.regiao,
            pessoas: pessoasDoGrupo,
            veiculo: original.veiculo || null,
            motorista: original.motorista || null,
          });
        });
        return;
      }

      if (n === 1) {
        // ✅ Um veículo só — mantém como está
        novosGrupos.push({ regiao: gr.regiao, pessoas: gr.pessoas, veiculo: null, motorista: null });
        return;
      }

      // ✅ Múltiplos veículos — agrupa por bairro primeiro para manter proximidade
      const porBairro = {};
      gr.pessoas.forEach(p => {
        const bairro = p.bairro || "Sem bairro";
        if (!porBairro[bairro]) porBairro[bairro] = [];
        porBairro[bairro].push(p);
      });

      const bairrosOrdenados = Object.entries(porBairro)
        .sort((a, b) => b[1].length - a[1].length);

      const pessoasOrdenadas = bairrosOrdenados.flatMap(([, pessoas]) => pessoas);

      const buckets = Array.from({ length: n }, () => []);
      pessoasOrdenadas.forEach((person, index) => {
        buckets[index % n].push(person);
      });

      buckets.forEach((pessoasDoGrupo) => {
        novosGrupos.push({ regiao: gr.regiao, pessoas: pessoasDoGrupo, veiculo: null, motorista: null });
      });
    });
    setGrupos(novosGrupos);
    setStep(3);
  };

  const setVeiculo = (idx, id) => setGrupos(prev => prev.map((g, i) => i === idx ? preserveReview(g, { ...g, veiculo: veiculosDisponiveis.find(v => v.id === Number(id)) || null }) : g));
  const setMotorista = (idx, id) => setGrupos(prev => prev.map((g, i) => i === idx ? preserveReview(g, { ...g, motorista: motoristasDisponiveis.find(m => m.id === Number(id)) || null }) : g));
  const setProgramExecutor = (idx, programId) => {
    const program = planningPrograms.find(
      (item) => Number(item.id) === Number(programId)
    );
    try {
      const otherGroups = [...grupos.filter((_, groupIndex) => groupIndex !== idx), ...(programRoute?.vans_enviadas || [])];
      const updated = attachProgramToVanGroup(grupos[idx], program, {
        usedMotoristaIds: otherGroups.map((group) => Number(group.motorista?.id)).filter(Boolean),
        usedVeiculoIds: otherGroups.map((group) => Number(group.veiculo?.id)).filter(Boolean),
      });
      setGrupos((current) => current.map((group, groupIndex) => (
        groupIndex === idx ? preserveReview(group, updated) : group
      )));
    } catch (error) {
      showError(error?.message || "Não foi possível trocar a PG de referência desta van.");
    }
  };

  const resetProgramDistribution = () => {
    if (!selectedPessoas.length) return;
    const usesRegions = selectedPessoas.some((person) => cidadeTemRegiao(person.cidade));
    setGruposRegiao(buildProgramRegionGroupsFromPeople(selectedPessoas, { usesRegions, cityHasRegions:cidadeTemRegiao }));
    setGrupos([]);
    setMarkedGroups(new Set());
    setStep(2);
  };

  const moveProgramPassenger = (fromGroupIndex, passengerKey, toGroupIndex) => {
    if (fromGroupIndex === toGroupIndex) return;

    const source = grupos[fromGroupIndex];
    const target = grupos[toGroupIndex];
    if (!source || !target) return;

    const capacity = target.veiculo ? Math.max(1, Number(target.veiculo.capacidade) || 15) : null;
    if (capacity != null && target.pessoas.length >= capacity) {
      showError(`A van do pedido ${target.programacao_codigo_responsavel || toGroupIndex + 1} já atingiu a capacidade (${capacity} lugares).`);
      return;
    }

    const personIndex = (source.pessoas || []).findIndex((person) => (
      String(person.programacao_passageiro_id || person.id) === String(passengerKey)
    ));
    if (personIndex < 0) return;
    if ((source.pessoas || []).length <= 1) {
      showError("Cada van precisa manter ao menos um passageiro.");
      return;
    }

    setGrupos((prev) => {
      const next = prev.map((group) => ({ ...group, pessoas: [...(group.pessoas || [])] }));
      const sourceGroup = next[fromGroupIndex];
      const targetGroup = next[toGroupIndex];
      const moveIndex = sourceGroup.pessoas.findIndex((person) => (
        String(person.programacao_passageiro_id || person.id) === String(passengerKey)
      ));
      if (moveIndex < 0) return prev;
      const [person] = sourceGroup.pessoas.splice(moveIndex, 1);
      targetGroup.pessoas.push(person);
      return withProgramGroupRegionLabels(next.map((group,index) => preserveReview(prev[index],group)));
    });
  };

  const normalizePlanningDistribution = (nextGroups) => {
    const nextNumber = Math.max(1, Number(programRoute?.payload?.proximo_numero_van) || 1,
      ...grupos.map(group => Number(group.planejamento_van_numero || 0) + 1));
    return withProgramGroupRegionLabels(numberPlanningGroups(nextGroups, {
      ...programRoute, payload: { ...programRoute?.payload, proximo_numero_van: nextNumber },
    }));
  };

  const changePlanningDistribution = (nextGroups) => {
    const next = normalizePlanningDistribution(nextGroups);
    setGrupos(next);
    setMarkedGroups(new Set());
    return next;
  };

  const savePlanningDistribution = async (nextGroups) => {
    setLoading(true);
    try {
      const saved = await persistDraft(normalizePlanningDistribution(nextGroups));
      setMarkedGroups(new Set());
      return { groups: withProgramGroupRegionLabels(saved.payload.grupos), version: `${saved.id}:${saved.edit_version || 0}` };
    } finally { setLoading(false); }
  };

  const salvarNoBacklog = async () => {
    setLoading(true);
    setPendingAction("backlog");
    try {
      const wasEditing = Boolean(editingDraft?.id);
      const savedRoute = await persistDraft(isPlanningPrograms && step === 2 ? buildPendingVanGroups() : null);
      if (isPlanningPrograms) {
        setToastType("success");
        setToast("Planejamento salvo. Nenhuma mensagem foi enviada.");
        return;
      }
      setRoutes(grupos.map((grupo) => ({ ...grupo, trecho })));
      setRouteOutcome({
        mode: "rascunho",
        routeId: savedRoute.id,
        isTestRoute,
        wasEditing,
        isProgramPlanning: isPlanningPrograms,
        preservedExternal: Number(savedRoute?.notification_reconciliation?.preserved_external) || 0,
      });
      setStep(4);
    } catch (e) {
      console.error(e);
      showError(e.message || "Erro ao salvar planejamento.");
    } finally {
      setLoading(false);
      setPendingAction("");
    }
  };

  const finishRouteDispatch = async (savedRoute, wasEditing, results) => {
    await finalizeRouteDispatch({ routeId: savedRoute.id });

    const expiredPreviousRoutes = (results || [])
      .flatMap((item) => item?.pendencias_anteriores_expiradas || [])
      .filter((item, index, all) => (
        all.findIndex((candidate) => Number(candidate.rota_id) === Number(item.rota_id)) === index
      ));
    if (wasEditing) {
      setEditingDraft(null);
      onClearDraftRoute?.();
    }
    setRoutes(results);
    setRouteOutcome({
      mode: "enviada",
      routeId: savedRoute.id,
      isTestRoute,
      wasEditing,
      expiredPreviousRoutes,
      preservedExternal: Number(savedRoute?.notification_reconciliation?.preserved_external) || 0,
    });
    setStep(4);
  };

  const runRouteDispatch = async (savedRoute, {
    ignorarValidacaoTelefone = false,
    ignorarValidacaoSgt = false,
    sgtSnapshotId = null,
    sgtOverrideMotivo = "",
  } = {}) => {
    try {
      const results = await dispatchRouteGroups({
        trecho,
        horario,
        data,
        idPedido: idPedidoLimpo,
        groups: grupos,
        routeId: savedRoute.id,
        ignorarValidacaoTelefone,
        ignorarValidacaoSgt,
        sgtSnapshotId,
        sgtOverrideMotivo,
      });
      return { ok: true, results };
    } catch (e) {
      if (e?.code === "TELEFONES_WHATSAPP_INVALIDOS" && !ignorarValidacaoTelefone) {
        return {
          ok: false,
          needsPhoneOverride: true,
          falhas: Array.isArray(e.falhas) ? e.falhas : [],
        };
      }
      if ((e?.code === "DIVERGENCIA_SGT" || e?.code === "CONFERENCIA_SGT_INDISPONIVEL") && !ignorarValidacaoSgt) {
        return {
          ok: false,
          needsSgtOverride: true,
          sgt: {
            code: e.code,
            motivo: e.motivo || null,
            conferencias: Array.isArray(e.conferencias) ? e.conferencias : [],
            snapshot: e.snapshot || null,
            ultimaTentativa: e.ultima_tentativa || null,
            grupoLabel: e.grupo_label || null,
            message: e.message,
          },
        };
      }
      throw e;
    }
  };

  const confirmDispatchIgnoringPhoneValidation = async () => {
    const current = phoneValidationDialog;
    if (!current?.savedRoute) return;

    setPhoneValidationDialog(null);
    setLoading(true);
    setPendingAction("gerar");
    try {
      const dispatchResult = await runRouteDispatch(current.savedRoute, { ignorarValidacaoTelefone: true });
      if (dispatchResult?.needsPhoneOverride) {
        showError("A validação de telefones ainda falhou. Tente novamente ou corrija os cadastros.");
        return;
      }
      if (dispatchResult?.needsSgtOverride) {
        recordUiError({
          message: "A rota diverge do pedido no SGT ou a conferência não pôde ser concluída.",
          code: dispatchResult.sgt?.code || "CONFERENCIA_SGT_INDISPONIVEL",
          page: "pedidos_montar_rota",
          source: "validation_dialog",
        });
        setSgtOverrideMotivo("");
        setSgtValidationDialog({
          savedRoute: current.savedRoute,
          wasEditing: current.wasEditing,
          ignorarValidacaoTelefone: true,
          sgt: dispatchResult.sgt,
        });
        return;
      }
      await finishRouteDispatch(current.savedRoute, current.wasEditing, dispatchResult.results);
    } catch (e) {
      console.error(e);
      const dispatchMessage = formatRouteDispatchError(e);
      const suffix = e?.code === "MOTORISTA_RESPOSTA_PENDENTE"
        ? " O planejamento foi salvo — nada foi enviado ao motorista."
        : e?.code === "TELEFONES_WHATSAPP_INVALIDOS"
          ? " O planejamento permanece disponível para você corrigir os cadastros."
          : " O planejamento foi mantido para nova tentativa. Os grupos já enviados não serão duplicados.";
      showError(dispatchMessage + suffix);
    } finally {
      setLoading(false);
      setPendingAction("");
    }
  };

  const confirmDispatchIgnoringSgtValidation = async () => {
    const current = sgtValidationDialog;
    if (!current?.savedRoute) return;

    setSgtValidationDialog(null);
    setLoading(true);
    setPendingAction("gerar");
    try {
      const dispatchResult = await runRouteDispatch(current.savedRoute, {
        ignorarValidacaoTelefone: Boolean(current.ignorarValidacaoTelefone),
        ignorarValidacaoSgt: true,
        sgtSnapshotId: current.sgt?.snapshot?.id || null,
        sgtOverrideMotivo: sgtOverrideMotivo.trim(),
      });
      if (dispatchResult?.needsPhoneOverride) {
        recordUiError({
          message: "Alguns telefones não passaram na validação do WhatsApp.",
          code: "TELEFONES_WHATSAPP_INVALIDOS",
          page: "pedidos_montar_rota",
          source: "validation_dialog",
          details: { count: dispatchResult.falhas?.length || 0 },
        });
        setPhoneValidationDialog({
          savedRoute: current.savedRoute,
          wasEditing: current.wasEditing,
          falhas: dispatchResult.falhas,
        });
        return;
      }
      await finishRouteDispatch(current.savedRoute, current.wasEditing, dispatchResult.results);
    } catch (e) {
      console.error(e);
      const dispatchMessage = formatRouteDispatchError(e);
      showError(`${dispatchMessage} O planejamento foi mantido para nova tentativa. Os grupos já enviados não serão duplicados.`);
    } finally {
      setLoading(false);
      setPendingAction("");
    }
  };

  const gerarRotas = async () => {
    setLoading(true);
    setPendingAction("gerar");
    let savedRoute = null;
    try {
      const wasEditing = Boolean(editingDraft?.id);
      savedRoute = await persistDraft();

      const dispatchResult = await runRouteDispatch(savedRoute);
      if (dispatchResult?.needsPhoneOverride) {
        recordUiError({
          message: "Alguns telefones não passaram na validação do WhatsApp.",
          code: "TELEFONES_WHATSAPP_INVALIDOS",
          page: "pedidos_montar_rota",
          source: "validation_dialog",
          details: { count: dispatchResult.falhas?.length || 0 },
        });
        setPhoneValidationDialog({
          savedRoute,
          wasEditing,
          falhas: dispatchResult.falhas,
        });
        return;
      }
      if (dispatchResult?.needsSgtOverride) {
        recordUiError({
          message: "A rota diverge do pedido no SGT ou a conferência não pôde ser concluída.",
          code: dispatchResult.sgt?.code || "CONFERENCIA_SGT_INDISPONIVEL",
          page: "pedidos_montar_rota",
          source: "validation_dialog",
        });
        setSgtOverrideMotivo("");
        setSgtValidationDialog({
          savedRoute,
          wasEditing,
          ignorarValidacaoTelefone: false,
          sgt: dispatchResult.sgt,
        });
        return;
      }

      await finishRouteDispatch(savedRoute, wasEditing, dispatchResult.results);
    } catch (e) {
      console.error(e);
      const dispatchMessage = formatRouteDispatchError(e);
      if (savedRoute?.id) {
        const suffix = e?.code === "MOTORISTA_RESPOSTA_PENDENTE"
          ? " O planejamento foi salvo — nada foi enviado ao motorista."
          : e?.code === "TELEFONES_WHATSAPP_INVALIDOS"
            ? " O planejamento permanece disponível para você corrigir os cadastros."
            : " O planejamento foi mantido para nova tentativa. Os grupos já enviados não serão duplicados.";
        showError(dispatchMessage + suffix);
      } else {
        showError(dispatchMessage || "Erro de conexão! O n8n está rodando?");
      }
    } finally {
      setLoading(false);
      setPendingAction("");
    }
  };

  const reset = () => requestLeave(() => {
    if (embedded && onBack) {
      onBack();
      return;
    }
    resetForm(true);
  });

  const usedMotoristas = [...grupos.map(g => g.motorista?.id), ...(programRoute?.vans_enviadas || []).map((van) => van.motorista?.id)].filter(Boolean);
  const usedVeiculos = [...grupos.map(g => g.veiculo?.id), ...(programRoute?.vans_enviadas || []).map((van) => van.veiculo?.id)].filter(Boolean);
  const pendingExecutorVans = grupos
    .map((group, index) => ({
      index,
      motorista: Number(group?.motorista?.id) > 0,
      veiculo: Number(group?.veiculo?.id) > 0,
    }))
    .filter((item) => !item.motorista || !item.veiculo);
  const allAssigned = grupos.length > 0 && pendingExecutorVans.length === 0;
  const readyForDispatch = vanReady;
  const dispatchIndexes = planningDispatchIndexes(grupos, markedGroups);
  const canReviewPlanning = dispatchIndexes.length > 0 && dispatchIndexes.every((index) => readyForDispatch(grupos[index]));
  const joinMarkedGroups = (selectedIndexes = [...markedGroups]) => {
    const indexes = [...new Set(selectedIndexes)].sort((a, b) => a - b);
    if (indexes.length < 2) return;
    const first = grupos[indexes[0]];
    const people = indexes.flatMap((index) => grupos[index].pessoas);
    const merged = preserveReview(first, { ...first, pessoas: people,
      veiculo: first.veiculo && Number(first.veiculo.capacidade || 15) >= people.length ? first.veiculo : null });
    setGrupos(withProgramGroupRegionLabels(grupos.flatMap((group, index) => index === indexes[0] ? [merged] : indexes.includes(index) ? [] : [group])));
    setMarkedGroups(new Set());
  };
  const splitPlanningVan = (index, count) => {
    const original = grupos[index];
    if (!original || !Number.isInteger(count) || count < 2 || count > original.pessoas.length) return;
    const divided = splitRegionGroupsIntoVanGroups([{ regiao: original.regiao, pessoas: original.pessoas, qtdVeiculos: count }], getPersonIdentity)
      .map((group, part) => part === 0 ? preserveReview(original, { ...original, pessoas: group.pessoas })
        : { ...group, programacao_responsavel_id: original.programacao_responsavel_id, data: original.data, horario: original.horario, trecho: original.trecho });
    const assigned = attachProgramsToVanGroups(divided, programPlanningContext.programs || [], {
      reservedMotoristaIds: [...grupos.filter((_, i) => i !== index), ...(programRoute?.vans_enviadas || [])].map(g => Number(g.motorista?.id)),
      reservedVeiculoIds: [...grupos.filter((_, i) => i !== index), ...(programRoute?.vans_enviadas || [])].map(g => Number(g.veiculo?.id)),
    });
    const numbered = numberPlanningGroups(assigned, { payload: { proximo_numero_van: Math.max(Number(programRoute?.payload?.proximo_numero_van) || 1, ...grupos.map(g => Number(g.planejamento_van_numero) + 1)) }, vans_enviadas: programRoute?.vans_enviadas || [] });
    setGrupos(withProgramGroupRegionLabels(grupos.flatMap((group, i) => i === index ? numbered : [group])));
    setMarkedGroups(new Set());
  };
  const reviewPlanning = async (selectedIndexes = dispatchIndexes) => {
    const indexes = Array.isArray(selectedIndexes) ? selectedIndexes : dispatchIndexes;
    if (!indexes.length || indexes.some(index => !readyForDispatch(grupos[index]))) {
      showError('Escolha uma van com motorista e veículo para conferir o envio.');
      return;
    }
    setLoading(true);
    try {
      const saved = await persistDraft();
      setPlanningReview({ route: saved, groupIds: indexes.map((index) => saved.payload.grupos[index].planejamento_grupo_id) });
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const sendSelectedPlanning = async (groupIds) => {
    setLoading(true);
    try {
      const result = planningReview.retryRouteId
        ? { results: [await retryPlanningVan({ routeId: planningReview.retryRouteId, onProgress: reportPlanningProgress })] }
        : await sendPlanningVans({ route: planningReview.route, groupIds, onProgress: reportPlanningProgress,
          onPrepared: (prepared) => {
            setProgramRoute(prepared.planejamento);
            setGrupos(withProgramGroupRegionLabels(prepared.planejamento.payload.grupos));
            setMarkedGroups(new Set()); setPlanningReview(null);
            setPlanningProgress(previous => ({ ...previous, ...Object.fromEntries(prepared.rotas.map(route => [route.id, { routeId: route.id, vanNumber: route.payload.grupos[0].planejamento_van_numero, status: 'queued' }])) }));
          },
        });
      setPlanningReview(null);
      const current = await refreshPlanning();
      setLastPlanningDispatchId(result.results[0]?.routeId || null);
      setToastType(result.results.some((item) => item.status === "failed") ? "error" : "success");
      setToast(planningDispatchMessage(result.results, current?.payload.grupos.length || 0));
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const reportPlanningProgress = (result) => setPlanningProgress(previous => ({ ...previous, [result.routeId]: result }));
  const refreshPlanning = async () => {
    const routeId = programRoute?.id || planningReview?.route?.id;
    if (!routeId) return null;
    const current = await apiFetch(`/rotas/${routeId}`);
    if (planningDirtyRef.current) {
      setProgramRoute(previous => ({ ...previous, vans_enviadas: current.vans_enviadas }));
    } else {
      if (current.edit_version !== programRoute?.edit_version) setMarkedGroups(new Set());
      setProgramRoute(current); setGrupos(withProgramGroupRegionLabels(current.payload.grupos));
    }
    return current;
  };
  const retryPlanningReview = async (van) => {
    setLoading(true);
    try {
      const current = await apiFetch(`/rotas/${van.id}`);
      setPlanningReview({ route: current, retryRouteId: van.id, groupIds: [current.payload.grupos[0].planejamento_grupo_id] });
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const openPlanningCorrection = async (van) => {
    setLoading(true);
    try {
      const current = await apiFetch(`/rotas/${van.id}`);
      if (current.tem_resposta_motorista) throw new Error('O motorista já respondeu. A execução está preservada; acompanhe os detalhes da van.');
      setCorrectionRoute(current);
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const savePlanningCorrection = async (group) => {
    setLoading(true);
    try {
      await apiFetch(`/rotas/${correctionRoute.id}`, { method: 'PUT', body: JSON.stringify({ ...correctionRoute,
        payload: { ...correctionRoute.payload, grupos: [group] }, motoristas: [group.motorista.nome],
        veiculos: [`${group.veiculo.veiculo} (${group.veiculo.placa})`], confirm_external_effects: Boolean(correctionRoute.tem_envio_iniciado),
      }) });
      setPlanningProgress(previous => { const next = { ...previous }; delete next[correctionRoute.id]; return next; });
      setCorrectionRoute(null); await refreshPlanning();
      setToastType('success'); setToast('Correção salva. Revise e retome o envio desta van.');
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const openPassengerEditor = (index) => {
    if (!programRoute?.id || hasUnsavedPlanningChanges) { setPassengerEditGate({ index }); return; }
    setPassengerEditor({ group: grupos[index], version: programRoute.edit_version });
  };
  const saveAndOpenPassengerEditor = async () => {
    setLoading(true);
    try {
      const saved = await persistDraft();
      const group = saved.payload.grupos[passengerEditGate.index];
      setPassengerEditGate(null);
      setPassengerEditor({ group, version: saved.edit_version });
    } catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const savePlanningPassengers = async (changes) => {
    setLoading(true);
    try {
      const current = await apiFetch(`/rotas/${programRoute.id}/planejamento/passageiros`, { method: 'PATCH', body: JSON.stringify({
        ...changes, grupo_id: passengerEditor.group.planejamento_grupo_id, edit_version: passengerEditor.version,
      }) });
      setProgramRoute(current); setGrupos(withProgramGroupRegionLabels(current.payload.grupos));
      setPlanningPrograms(current.programacoes || planningPrograms);
      setMarkedGroups(new Set()); setPassengerEditor(null);
      setToastType('success'); setToast('Passageiros salvos. Confira esta van novamente antes de enviar.');
    } finally { setLoading(false); }
  };
  const setGroupReviewed = (index, checked) => setGrupos(previous => previous.map((group,i) => i === index
    ? { ...group, conferida: checked, conferencia_solicitada: checked, conferida_em: checked ? group.conferida_em : null, conferida_por: checked ? group.conferida_por : null } : group));
  const saveAndLeave = async () => {
    setLoading(true);
    try { await persistDraft(step === 2 ? buildPendingVanGroups() : null); const action = leaveAction.afterSave || leaveAction.run; setLeaveAction(null); action(); }
    catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };
  const savePlanningAndLeave = async () => {
    if (programRoute && !hasUnsavedPlanningChanges) { onPlanningComplete?.(); return; }
    setLoading(true);
    try { await persistDraft(step === 2 ? buildPendingVanGroups() : null); onPlanningComplete?.(); }
    catch (error) { showError(error.message); }
    finally { setLoading(false); }
  };

  // ── Quadro de vans ──────────────────────────────────────────────────────────
  const boardMode = isPlanningPrograms && step === 3 && !detailedWorkspace;
  const moveBoardPassengers = (keys, destination) => {
    try {
      const target = destination === 'new' ? null : grupos[Number(destination)];
      const result = previewPassengerTransfer(grupos, keys, destination);
      changePlanningDistribution(result.groups);
      setBoardTouched(true);
      const removed = result.removed.length
        ? ` ${result.removed.length === 1 ? `A van ${result.removed[0]} ficou vazia e saiu` : `As vans ${result.removed.join(', ')} ficaram vazias e saíram`} da montagem.` : '';
      setToastType('success');
      setToast(`${result.moved} ${result.moved === 1 ? 'pessoa levada' : 'pessoas levadas'} para ${target ? `a van ${Number(target.planejamento_van_numero) || Number(destination) + 1}` : 'uma nova van'}.${removed}`);
      return true;
    } catch (error) {
      showError(error.message);
      return false;
    }
  };
  const setBoardDriver = (index, id) => { setMotorista(index, id); setBoardTouched(true); };
  const setBoardVehicle = (index, id) => { setVeiculo(index, id); setBoardTouched(true); };
  // Salva a montagem em segundo plano. Uma edição feita durante o salvamento
  // não é sobrescrita pela resposta: ela segue para o próximo salvamento.
  const autosavePlanning = async () => {
    autosaveBusy.current = true;
    setAutosave({ status: 'saving' });
    const snapshot = grupos;
    const fingerprint = groupsFingerprint(snapshot);
    try {
      const result = await apiFetch(programRoute?.id ? `/rotas/${programRoute.id}/planejamento` : "/programacoes/planejar", {
        method: programRoute?.id ? "PATCH" : "POST",
        body: JSON.stringify({
          programacao_ids: programPlanningContext.programacaoIds,
          grupos: snapshot,
          cliente_id: selectedEmpresa?.cliente_id || null,
          replanejar_rota_id: programPlanningContext.replanejarRotaId || programRoute?.id || null,
          edit_version: programRoute?.edit_version,
        }),
      });
      const saved = result.rota || result;
      setProgramRoute(saved);
      setGrupos((current) => (groupsFingerprint(current) === fingerprint ? withProgramGroupRegionLabels(saved.payload.grupos) : current));
      setAutosave({ status: 'saved', at: new Date() });
    } catch (error) {
      setAutosave({ status: 'error', message: error.message || 'erro de conexão.' });
    } finally {
      autosaveBusy.current = false;
      setAutosaveTick((value) => value + 1);
    }
  };
  useEffect(() => {
    if (!boardMode || !(boardTouched || programRoute?.id) || !hasUnsavedPlanningChanges) return undefined;
    if (loading || planningReview || passengerEditor || autosaveBusy.current || autosave.status === 'error') return undefined;
    const timer = setTimeout(() => { void autosavePlanning(); }, 900);
    return () => clearTimeout(timer);
  }, [boardMode, boardTouched, programRoute?.id, hasUnsavedPlanningChanges, grupos, loading, planningReview, passengerEditor, autosave.status, autosaveTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const STEPS =isPlanningPrograms ? ["Pedidos", `Distribuir por ${groupingLabel}`, "Montar vans"] : ["Selecionar", `Distribuir por ${groupingLabel}`, "Atribuir Veículo", "Resultado"];
  const pgVanTarget = isPlanningPrograms ? (programPlanningContext?.programacaoIds?.length || 0) : 0;
  const totalVeiculosAlocados = gruposRegiao.reduce((sum, group) => sum + (Number(group.qtdVeiculos) || 1), 0);
  const programVanCountValid = !isPlanningPrograms
    || (totalVeiculosAlocados >= 1 && totalVeiculosAlocados <= selectedPessoas.length);
  const isStepComplete = (index) => {
    if (isPlanningPrograms && index === 0) return step >= 2;
    return step > index + 1;
  };
  const isStepActive = (index) => {
    if (isPlanningPrograms && index === 0) return false;
    return step === index + 1;
  };
  const rotaLabel = formatTrechoLabel(trecho, routeConfig);
  const destinoLabel = getDestinoLabel(routeConfig);

  const handleTrocarEmpresa = () => {
    if (selected.size > 0 || step > 1) {
      showError("Limpe a seleção e volte ao início antes de trocar a empresa.");
      return;
    }
    setEmpresaDestinoId("");
    setStep(1);
  };

  const dataFormatada = data
    ? new Date(data + 'T12:00:00').toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })
    : "";

  useEffect(() => {
    if (embedded && isPlanningPrograms && programRoute?.id) writeLocation({ montagem: programRoute.id }, { replace: true });
  }, [embedded, isPlanningPrograms, programRoute?.id]);

  return (
    <div className="fade-in">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div style={{ flex: "1 1 180px", minWidth: 0 }}>
          <PageTitle icon="route">
            {isPlanningPrograms ? (step === 2 ? "Distribuir por região" : "Montar vans") : (isEditingDraft ? "Editar planejamento" : "Planejar vans")}
          </PageTitle>
          {!isPlanningPrograms && <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>
            {canShowRouteBuilder ? rotaLabel : needsEmpresaSelection ? "Escolha a empresa destino para começar" : "Montagem de rota"}
          </p>}
          {!isPlanningPrograms && selectedEmpresa && empresasDestino.length > 1 && canShowRouteBuilder && (
            <p style={{ color: "var(--accent)", fontSize: '0.9375rem', marginTop: 6 }}>
              Empresa: <strong>{selectedEmpresa.nome}</strong>
              {step === 1 && selected.size === 0 && (
                <button
                  type="button"
                  onClick={handleTrocarEmpresa}
                  style={{ marginLeft: 10, background: "none", border: "none", color: "#93c5fd", cursor: "pointer", fontSize: '0.9375rem', textDecoration: "underline", padding: 0 }}
                >
                  trocar
                </button>
              )}
            </p>
          )}
          {isEditingDraft && (
            <p style={{ color: "#93c5fd", fontSize: '0.9375rem', marginTop: 6 }}>
              Editando rascunho {editingDraft?.id_pedido ? `#${editingDraft.id_pedido}` : `#${editingDraft?.id}`}.
            </p>
          )}
          {isPlanningPrograms && !boardMode && (
            <p style={{ color: "var(--muted)", fontSize: '0.9375rem', marginTop: 6, lineHeight: 1.5 }}>
              {pgVanTarget === 1 ? <>PG <strong>{idPedidoLimpo}</strong></> : <><strong>{pgVanTarget}</strong> pedidos · PGs: <strong>{idPedidoLimpo}</strong></>}
            </p>
          )}
        </div>
      {embedded && <div className="planning-back-navigation"><Btn variant="secondary" href={locationHref({montagem:null})} disabled={loading} aria-label={programRoute ? "Voltar às viagens" : "Voltar aos pedidos"}
        onClick={() => requestLeave(() => programRoute ? onPlanningComplete?.() : onBack?.(), () => onPlanningComplete?.())}>
        <Icon name="back" size={18} /><span className="planning-back-long">{programRoute ? 'Voltar às viagens' : 'Voltar aos pedidos'}</span><span className="planning-back-short">Voltar</span>
      </Btn></div>}

        {!isPlanningPrograms && (step > 1 || isEditingDraft) && (
          <Btn variant="secondary" onClick={reset}>
            ↩ {isEditingDraft ? "Sair da edição" : isPlanningPrograms ? "Fechar montagem" : "Recomeçar"}
          </Btn>
        )}
      </div>

      {isEditingDraft && editConflict && (
        <div role="alert" style={{ color: "#fecaca", marginBottom: 16 }}>
          {editConflict}
        </div>
      )}

      {isEditingDraft && (
        <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.22)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, color: "#bfdbfe", fontSize: '1rem' }}>
          {editingDraft?.hadDispatch
            ? "Esta rota já foi disparada, mas o motorista ainda não respondeu. Ao salvar, a solicitação anterior será encerrada no sistema e você precisará confirmar novamente. Mensagens que já chegaram ao WhatsApp não podem ser recolhidas; oriente o motorista a considerar somente a correção mais recente."
            : "Este planejamento ainda não foi confirmado. Você pode ajustar passageiros, horário, data, regiões, motorista e veículo antes da confirmação."}
        </div>
      )}

      {(empresasLoading || !geographyReady) && (
        <Card style={{ padding: 40, textAlign: "center", marginBottom: 20 }}>
          <Spinner />
          <p style={{ color: "var(--muted)", marginTop: 12 }}>Carregando empresas destino...</p>
        </Card>
      )}

      {!empresasLoading && empresasDestino.length === 0 && (
        <Card style={{ padding: 28, marginBottom: 20 }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 8 }}>Nenhuma empresa destino cadastrada</h3>
          <p style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.6 }}>
            Cadastre ao menos uma empresa em <strong>Empresas de destino</strong> antes de montar rotas.
          </p>
        </Card>
      )}

      {needsEmpresaSelection && (
        <Card style={{ padding: 24, marginBottom: 20 }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: 8 }}>Para qual empresa é esta rota?</h3>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginBottom: 18, lineHeight: 1.6 }}>
            Selecione a empresa destino. Só serão listados funcionários vinculados a ela.
          </p>
          <div style={{ display: "grid", gap: 10 }}>
            {empresasDestino.map((empresa) => (
              <button
                key={empresa.id}
                type="button"
                onClick={() => {
                  setEmpresaDestinoId(String(empresa.id));
                  setSelected(new Set());
                  setStep(1);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                  background: "var(--s2)",
                  color: "var(--text)",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: '1.375rem' }}><Icon name="building" /></span>
                <span>
                  <div style={{ fontWeight: 700 }}>{empresa.nome}</div>
                  {empresa.endereco && (
                    <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>{empresa.endereco}</div>
                  )}
                </span>
              </button>
            ))}
          </div>
        </Card>
      )}

      {canShowRouteBuilder && (
        <>
          {/* Stepper: versão compacta no mobile, completa no desktop */}
          {!isPlanningPrograms && (isMobile ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28, background: "var(--s2)", borderRadius: 10, padding: "10px 14px", border: "1px solid var(--border)" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: '0.8125rem', fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                {step}
              </div>
              <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: "var(--text)" }}>{STEPS[step - 1]}</span>
              <span style={{ fontSize: '0.875rem', color: "var(--muted)", marginLeft: "auto" }}>{step} / {STEPS.length}</span>
            </div>
          ) : (
            <div style={{ display: "flex", marginBottom: 28, width: "100%" }}>
              {STEPS.map((s, i) => (
                <div key={s} style={{ display: "flex", alignItems: "center", flex: i === STEPS.length - 1 ? "none" : 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: isStepComplete(i) ? "var(--accent)" : isStepActive(i) ? "rgba(249,115,22,0.2)" : "var(--s3)", border: isStepActive(i) ? "2px solid var(--accent)" : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: '0.8125rem', fontWeight: 700, color: isStepComplete(i) ? "#fff" : isStepActive(i) ? "var(--accent)" : "var(--muted)", flexShrink: 0 }}>
                      {isStepComplete(i) ? "✓" : i + 1}
                    </div>
                    <span style={{ fontSize: '0.875rem', fontWeight: isStepActive(i) ? 600 : 400, color: isStepActive(i) ? "var(--text)" : "var(--muted)", whiteSpace: "nowrap" }}>{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div style={{ height: 1, flex: 1, background: isStepComplete(i) ? "var(--accent)" : "var(--border)", margin: "0 12px" }} />}
                </div>
              ))}
            </div>
          ))}

          {/* Step 1 */}
          {step === 1 && (
            <>
              {/* Dados da viagem */}
              <Card style={{ marginBottom: 20 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 14 }}>
                  Dados da viagem
                </div>

                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "flex-start" : "flex-end", flexWrap: "wrap" }}>
                  <div style={{ flexShrink: 0 }}>
                    <TrechoToggle trecho={trecho} setTrecho={setTrecho} destinoLabel={destinoLabel} />
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label htmlFor="rota-data" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500, letterSpacing: 0.3, textTransform: "uppercase" }}>Data da rota</label>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--s2)", border: "1px solid var(--border)", padding: "6px 14px", borderRadius: 12, transition: "border-color 0.2s", cursor: "pointer" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                    >
                      <span style={{ fontSize: '1rem' }}><Icon name="history" /></span>
                      <input id="rota-data" type="date" value={data} onChange={e => setData(e.target.value)}
                        style={{ background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: '1rem', fontWeight: 650, cursor: "pointer", colorScheme: "dark", padding: 0 }} />
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label htmlFor="rota-horario" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500, letterSpacing: 0.3, textTransform: "uppercase" }}>Horário</label>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--s2)", border: "1px solid var(--border)", padding: "6px 14px", borderRadius: 12, transition: "border-color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                    >
                      <Icon name="history" size={16} />
                      <input id="rota-horario" type="time" value={horario} onChange={e => setHorario(e.target.value)}
                        style={{ background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: '1rem', fontWeight: 650, cursor: "pointer", colorScheme: "dark", padding: 0 }} />
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label htmlFor="rota-codigo" style={{ fontSize: '0.9375rem', color: "var(--muted)", fontWeight: 500, letterSpacing: 0.3, textTransform: "uppercase" }}>Código do pedido</label>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--s2)", border: "1px solid var(--border)", padding: "6px 14px", borderRadius: 12, transition: "border-color 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = "var(--accent)"}
                      onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                    >
                      <input id="rota-codigo" type="text" value={idPedido} onChange={e => setIdPedido(e.target.value)}
                        placeholder="Código do pedido"
                        style={{ background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: '1rem', fontWeight: 600, padding: 0, width: 120 }} />
                    </div>
                  </div>

                  <label style={{
                    display: isPlanningPrograms ? "none" : "inline-flex", alignItems: "center", gap: 10, cursor: "pointer",
                    background: isTestRoute ? "rgba(249,115,22,0.10)" : "transparent",
                    border: isTestRoute ? "1px solid rgba(249,115,22,0.35)" : "1px solid var(--border)",
                    borderRadius: 20, padding: "6px 14px", transition: "all 0.2s",
                    marginLeft: isMobile ? 0 : "auto", flexShrink: 0,
                  }}>
                    <input type="checkbox" checked={isTestRoute} onChange={(e) => setIsTestRoute(e.target.checked)}
                      style={{ accentColor: "var(--accent)", width: 14, height: 14, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: isTestRoute ? "var(--accent)" : "var(--muted)" }}>
                      Rota de teste
                    </span>
                  </label>
                </div>
              </Card>

              <Card style={{ marginBottom: 16 }}>
                {/* Filtros: row no desktop, column no mobile */}
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "stretch" : "center" }}>
                  <Input placeholder="Buscar funcionário..." value={search} onChange={handleSearch} style={{ flex: isMobile ? undefined : 1 }} />
                  {hasRegionalPeople && (
                    <Select value={regiaoFilter} onChange={handleRegiaoFilter} style={{ width: isMobile ? "100%" : 180 }}>
                      <option value="">Todas as regiões</option>
                      {REGIAO.map(r => <option key={r} value={r}>{r}</option>)}
                    </Select>
                  )}
                  <div style={{ flexShrink: 0, background: "var(--s3)", borderRadius: 8, padding: "10px 14px", fontSize: '1rem', fontFamily: "var(--font-mono)" }}>
                    <span style={{ color: "var(--accent)", fontWeight: 700 }}>{selected.size}</span>
                    <span style={{ color: "var(--muted)" }}> selecionados</span>
                  </div>
                </div>
              </Card>

              <Card style={{ padding: 0, overflow: "visible", marginBottom: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "12px 16px", width: 40 }}>
                        <input type="checkbox" aria-label="Selecionar todos os passageiros deste filtro" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} style={{ cursor: "pointer", accentColor: "var(--accent)", width: 16, height: 16 }} />
                      </th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Nome</th>
                      {/* Endereço oculto no mobile */}
                      {!isMobile && <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Endereço</th>}
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Cidade</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Bairro</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase" }}>Região</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={isMobile ? 4 : 5} style={{ padding: 40, textAlign: "center", color: "var(--muted)" }}>Nenhum funcionário encontrado</td></tr>}
                    {paginated.map((p, i) => (
                      <tr key={p.id} style={{ borderBottom: i < paginated.length - 1 ? "1px solid var(--border)" : "none", background: selected.has(p.id) ? "rgba(249,115,22,0.05)" : "transparent", cursor: "pointer", transition: "background 0.1s" }} onClick={() => toggleOne(p.id)}>
                        <td style={{ padding: "12px 16px" }}><input type="checkbox" aria-label={`Selecionar passageiro ${p.nome}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} onClick={e => e.stopPropagation()} style={{ cursor: "pointer", accentColor: "var(--accent)", width: 16, height: 16 }} /></td>
                        <td style={{ padding: "12px 16px", fontWeight: 500 }}>
                          <div>{p.nome}</div>
                          {p.__draftOnly && (
                            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: "#fde68a" }}>
                              Presente apenas no rascunho salvo
                            </div>
                          )}
                          {p.id_pedido_origem && (
                            <div style={{ marginTop: 4, fontSize: '0.8125rem', color: "var(--muted)" }}>
                              Pedido: {p.id_pedido_origem}
                            </div>
                          )}
                        </td>
                        {!isMobile && <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.rua}, {p.numero}</td>}
                        <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.cidade}</td>
                        <td style={{ padding: "12px 16px", color: "var(--muted)", fontSize: '0.9375rem' }}>{p.bairro}</td>
                        <td style={{ padding: "12px 16px" }}>{cidadeTemRegiao(p.cidade) && p.regiao && <RegiaoBadge regiao={p.regiao} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
              <Card style={{ padding: 0, marginBottom: 16 }}>
                <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
              </Card>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn size="lg" onClick={handleAvancarStep2} disabled={!canAdvanceStep2}>
                  Distribuir por {selectedUsesRegions ? "Região" : "Cidade"} →
                </Btn>
              </div>
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (isPlanningPrograms ? <PlanningRegions regions={gruposRegiao} programs={planningPrograms} showBack={!embedded}
            onChange={setGruposRegiao} onGroup={abrirModalAgrupar} onUngroup={desagruparRegioes} onBuild={handleAvancarStep3}
            onSave={salvarNoBacklog} onLeave={() => requestLeave(() => programRoute ? onPlanningComplete?.() : onBack?.(), () => onPlanningComplete?.())} loading={loading} saved={Boolean(programRoute)} dirty={hasUnsavedPlanningChanges}
            destination={getDestinoLabel(routeConfig)} date={`${dataFormatada} · ${horario} · ${trecho === 'ida' ? 'Ida' : 'Volta'}`}
            capacity={Math.max(0,...veiculosDisponiveis.map(vehicle => Number(vehicle.capacidade)||0)) || null} usesRegions={selectedUsesRegions}
            published={programRoute?.vans_enviadas || []} /> : (
            <>
              <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <span><Icon name="route" /></span>
                <span style={{ fontSize: '1rem', color: "#93c5fd" }}>
                  Trecho: <strong>{formatTrechoCurto(trecho, routeConfig)}</strong>
                  {dataFormatada && <> · <Icon name="history" /> <strong>{dataFormatada}</strong></>}
                  { " · " }<Icon name="history" size={14} /> <strong>{horario}</strong>
                  {" "}· Defina quantos veículos vão buscar cada {groupingLabel.toLowerCase()}.
                  {isPlanningPrograms && (
                    <> · Total de vans: <strong style={{ color: programVanCountValid ? "#86efac" : "#fca5a5" }}>{totalVeiculosAlocados}</strong> para {pgVanTarget} PG(s)</>
                  )}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
                <Btn variant="secondary" onClick={abrirModalAgrupar}>
                  <Icon name="people" /> Agrupar {groupingPluralLabel}
                </Btn>
              </div>
              <div style={{ display: "grid", gap: 14, marginBottom: 24 }}>
                {gruposRegiao.map((gr, i) => {
                  const s = REGIAO_COLORS[gr.regiao] || REGIAO_COLORS["Sem região"];
                  const porVeiculo = Math.ceil(gr.pessoas.length / gr.qtdVeiculos);
                  const ehAgrupado = (gr.origens || []).length > 1;
                  const maxVeiculos = isPlanningPrograms
                    ? gr.pessoas.length
                    : Math.min(gr.pessoas.length, 5);
                  return (
                    <Card key={gr.id}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 36, height: 36, background: s.bg, border: `1px solid ${s.color}40`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1rem' }}><Icon name="route" /></div>
                          <div>
                            <div style={{ fontWeight: 700 }}>{gr.regiao}</div>
                            <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>{gr.pessoas.length} pessoa{gr.pessoas.length !== 1 ? "s" : ""} · ~{porVeiculo} por veículo</div>
                            {ehAgrupado && (
                              <div style={{ fontSize: '0.875rem', color: "var(--accent)", marginTop: 4 }}>
                                Agrupa: {(gr.origens || []).join(", ")}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {ehAgrupado && (
                            <Btn variant="secondary" size="sm" onClick={() => desagruparRegioes(gr.id)}>
                              Desagrupar
                            </Btn>
                          )}
                          <span style={{ fontSize: '0.9375rem', color: "var(--muted)" }}>Veículos:</span>
                          <select
                            value={gr.qtdVeiculos}
                            aria-label={`Quantidade de veículos para ${gr.regiao}`}
                            onChange={e => setGruposRegiao(prev => prev.map((g, idx) => idx === i ? { ...g, qtdVeiculos: Number(e.target.value) } : g))}
                            style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: "var(--text)", fontSize: '1rem', cursor: "pointer" }}>
                            {Array.from({ length: maxVeiculos }, (_, k) => k + 1).map(n => (
                              <option key={n} value={n}>{n} veículo{n > 1 ? "s" : ""}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {gr.pessoas.map(p => <span key={p.id} style={{ background: "var(--s3)", borderRadius: 6, padding: "3px 10px", fontSize: '0.875rem', color: "var(--muted)" }}>{p.nome}</span>)}
                      </div>
                    </Card>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                {isPlanningPrograms ? (
                  <Btn variant="secondary" disabled={loading} onClick={savePlanningAndLeave}>Salvar e sair</Btn>
                ) : (
                  <Btn variant="secondary" onClick={() => setStep(1)}><Icon name="back" size={16} /> Voltar</Btn>
                )}
                <Btn
                  size="lg"
                  onClick={handleAvancarStep3}
                  style={{ opacity: programVanCountValid ? 1 : 0.45, pointerEvents: programVanCountValid ? "auto" : "none" }}
                >
                  {isPlanningPrograms ? 'Aplicar distribuição' : 'Atribuir Veículos →'}
                </Btn>
              </div>
            </>
          ))}

          {/* Step 3 */}
          {step === 3 && (isPlanningPrograms ? (boardMode ? <PlanningBoard groups={grupos} published={programRoute?.vans_enviadas || []}
            drivers={motoristasDisponiveis} vehicles={veiculosDisponiveis} loading={loading}
            referenceCapacity={typicalCapacity(veiculosDisponiveis.map((vehicle) => vehicle.capacidade))}
            saveState={autosave}
            orders={planningPrograms.map((program) => ({ id: program.id, codigo: program.codigo, passageiros: (program.passageiros || []).length }))}
            tripLabel={`${trecho === 'ida' ? 'Ida' : 'Volta'} ${planningTimeRange(planningPrograms, horario)} para ${getDestinoLabel(routeConfig)}, ${new Date(`${data}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit' })}`}
            passengerTotal={grupos.reduce((sum, group) => sum + group.pessoas.length, 0) + (programRoute?.vans_enviadas || []).reduce((sum, van) => sum + (van.grupo?.pessoas?.length || 0), 0)}
            onDriver={setBoardDriver} onVehicle={setBoardVehicle} onMove={moveBoardPassengers}
            onSendVans={(indexes) => reviewPlanning(indexes)} onFinish={savePlanningAndLeave}
            onEditPassengers={openPassengerEditor} onRetrySave={() => setAutosave({ status: 'idle' })}
            onDetailed={() => setDetailedWorkspace(true)}
            onOpenPublished={(routeId) => requestLeave(() => onPlanningComplete?.({ routeId }))}
          /> : <>
            <div style={{ marginBottom: 12 }}><Btn size="sm" variant="secondary" disabled={loading || distributionPending} onClick={() => setDetailedWorkspace(false)}><Icon name="back" size={15} /> Voltar ao quadro de vans</Btn></div>
            <PlanningWorkspace groups={grupos} published={programRoute?.vans_enviadas || []}
            programs={planningPrograms} drivers={motoristasDisponiveis} vehicles={veiculosDisponiveis}
            marked={markedGroups} loading={loading} dirty={hasUnsavedPlanningChanges} saved={Boolean(programRoute)} progress={planningProgress}
            destination={getDestinoLabel(routeConfig)} date={new Date(`${data}T12:00:00`).toLocaleDateString('pt-BR')}
            onToggle={(index) => setMarkedGroups(previous => { const next = new Set(previous); next.has(index) ? next.delete(index) : next.add(index); return next; })}
            onDriver={setMotorista} onVehicle={setVeiculo} onReference={setProgramExecutor} onReviewed={setGroupReviewed} onMove={moveProgramPassenger}
            onMergeIndexes={joinMarkedGroups} onSplitVan={splitPlanningVan} onEditPassengers={openPassengerEditor}
            onDistributionChange={changePlanningDistribution} onSaveDistribution={savePlanningDistribution} distributionPending={distributionPending} onDistributionPending={setDistributionPending}
            distributionVersion={`${programRoute?.id || 'new'}:${programRoute?.edit_version || 0}`}
            onSave={salvarNoBacklog} onReview={() => reviewPlanning()} onReviewVan={index => reviewPlanning([index])} reviewCount={dispatchIndexes.length} canReview={canReviewPlanning}
            onDistribute={() => { setGruposRegiao(buildDraftRegionGroups(grupos)); setStep(2); }}
            onLeave={savePlanningAndLeave}
            onEditProgram={onEditProgram ? program => requestLeave(() => onEditProgram(program)) : undefined}
            onUndoPlanning={onUndoPlanning ? program => requestLeave(() => onUndoPlanning(program)) : undefined}
            onRefresh={async () => { setLoading(true); try { await refreshPlanning(); } catch (error) { showError(error.message); } finally { setLoading(false); } }}
            onRetry={retryPlanningReview} onCorrect={openPlanningCorrection} onOpen={(routeId) => requestLeave(() => onPlanningComplete?.({ routeId }))}
          /></>) : (
            <>
              {isPlanningPrograms && (
                <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#bbf7d0", fontSize: '0.9375rem', lineHeight: 1.55, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <span>
                    Ajuste os passageiros entre as vans usando o seletor em cada nome, se necessário.
                    Os pedidos de origem continuam vinculados aos passageiros. Escolha o motorista e o veículo de cada van.
                  </span>
                  {grupos.length > 1 && (
                    <Btn variant="secondary" size="sm" onClick={resetProgramDistribution} style={{ flexShrink: 0 }}>
                      ↺ Redistribuir por região
                    </Btn>
                  )}
                </div>
              )}
              <div style={{ background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, padding: "12px 16px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span><Icon name="route" /></span>
                <span style={{ fontSize: '1rem', color: "#93c5fd", lineHeight: 1.6 }}>
                  Trecho: <strong>{formatTrechoCurto(trecho, routeConfig)}</strong>
                  {dataFormatada && <> · <Icon name="history" /> <strong>{dataFormatada}</strong></>}
                  { " · " }<Icon name="history" size={14} /> <strong>{horario}</strong>
                  {idPedidoLimpo && <> · Prog. <strong>{idPedidoLimpo}</strong></>}
                  {" "}· <strong>{selectedPessoas.length}</strong> pessoa{selectedPessoas.length !== 1 ? "s" : ""}
                  {" "}· <strong>{grupos.length}</strong> {isPlanningPrograms ? (grupos.length === 1 ? "van" : "vans") : (grupos.length === 1 ? "grupo" : "grupos")}
                </span>
              </div>
              <div style={{ display: "grid", gap: 14, marginBottom: 24 }}>
                {grupos.map((g, i) => {
                  const s = REGIAO_COLORS[g.regiao] || REGIAO_COLORS["Sem região"];
                  const gruposDaRegiao = grupos.filter(x => x.regiao === g.regiao);
                  const sufixo = gruposDaRegiao.length > 1 ? ` — Grupo ${gruposDaRegiao.indexOf(g) + 1}` : "";

                  // ✅ Mostra os bairros distintos do grupo para o operador confirmar que faz sentido
                  const bairrosDoGrupo = [...new Set(g.pessoas.map(p => p.bairro).filter(Boolean))];

                  return (
                    <Card key={i}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          {isPlanningPrograms && <input type="checkbox" aria-label={`Selecionar van ${i + 1}`}
                            checked={markedGroups.has(i)} onChange={(event) => setMarkedGroups((previous) => {
                              const next = new Set(previous); event.target.checked ? next.add(i) : next.delete(i); return next;
                            })} />}
                          <div style={{ width: 36, height: 36, background: s.bg, border: `1px solid ${s.color}40`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1rem' }}><Icon name="fleet" /></div>
                          <div>
                            <div style={{ fontWeight: 700 }}>{isPlanningPrograms ? `Van ${i + 1} · ` : ""}{g.regiao}{sufixo}</div>
                            {g.programacao_codigo_responsavel && (
                              <div style={{ fontSize: '0.8125rem', color: "#86efac", marginTop: 2 }}>PG de referência da van: {g.programacao_codigo_responsavel}</div>
                            )}
                            <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>
                              {g.pessoas.length} pessoa{g.pessoas.length !== 1 ? "s" : ""}
                              {bairrosDoGrupo.length > 0 && (
                                <span style={{ marginLeft: 6, color: "var(--accent)" }}>
                                  · {bairrosDoGrupo.join(", ")}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Selects: lado a lado no desktop, empilhados no mobile */}
                        <div style={{ display: "flex", gap: 10, flexDirection: isMobile ? "column" : "row", width: isMobile ? "100%" : "auto" }}>
                          {isPlanningPrograms && grupos.length === 1 && (
                            <div
                              data-testid="single-van-traceability"
                              style={{
                                background: "rgba(34,197,94,.08)",
                                border: "1px solid rgba(34,197,94,.2)",
                                borderRadius: 8,
                                color: "#bbf7d0",
                                fontSize: '0.875rem',
                                lineHeight: 1.45,
                                maxWidth: isMobile ? "100%" : 380,
                                padding: "8px 10px",
                              }}
                            >
                              <strong>Uma única van</strong> para as PGs{" "}
                              {(programPlanningContext?.programs || []).map((program) => program.codigo).join(", ")}.
                              {" "}Pedido de referência: PG {g.programacao_codigo_responsavel}.
                            </div>
                          )}
                          {isPlanningPrograms && grupos.length > 1 && (
                            <select
                              value={g.programacao_responsavel_id || ""}
                              onChange={(event) => setProgramExecutor(i, event.target.value)}
                              aria-label={`Rastreabilidade da van ${i + 1}`}
                              style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: "var(--text)", fontSize: '0.9375rem', cursor: "pointer", width: isMobile ? "100%" : "auto" }}
                            >
                              {(programPlanningContext?.programs || []).map((program) => {
                                const rawCapacity = Number(program.veiculo_responsavel_capacidade);
                                const capacity = Number.isFinite(rawCapacity) && rawCapacity > 0 ? rawCapacity : null;
                                const exceedsCapacity = capacity != null && g.pessoas.length > capacity;
                                const driverLabel = program.motorista_responsavel_nome || "motorista a definir";
                                const vehicleLabel = program.veiculo_responsavel_nome || "veículo a definir";
                                return (
                                  <option
                                    key={program.id}
                                    value={program.id}
                                  >
                                    Referência PG {program.codigo} · {driverLabel} · {vehicleLabel}
                                    {exceedsCapacity
                                        ? ` (veículo sugerido suporta ${capacity}; escolha outro)`
                                        : ""}
                                  </option>
                                );
                              })}
                            </select>
                          )}
                          <select
                            value={g.veiculo?.id || ""}
                            onChange={e => setVeiculo(i, e.target.value)}
                            aria-label={`Veículo da van ${i + 1}`}
                            style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: g.veiculo ? "var(--text)" : "var(--muted)", fontSize: '0.9375rem', cursor: "pointer", width: isMobile ? "100%" : "auto" }}
                          >
                            <option value="">Selecionar veículo</option>
                            {veiculosDisponiveis.map((v) => {
                              const used = usedVeiculos.includes(v.id) && g.veiculo?.id !== v.id;
                              const capacity = Math.max(1, Number(v.capacidade) || 15);
                              const tooSmall = g.pessoas.length > capacity;
                              return (
                                <option key={v.id} value={v.id} disabled={used || tooSmall}>
                                  {v.veiculo} — {v.placa} · {capacity} lugares
                                  {used ? " (em uso)" : tooSmall ? " (capacidade insuficiente)" : ""}
                                </option>
                              );
                            })}
                          </select>
                          <select
                            value={g.motorista?.id || ""}
                            onChange={e => setMotorista(i, e.target.value)}
                            aria-label={`Motorista da van ${i + 1}`}
                            style={{ background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", color: g.motorista ? "var(--text)" : "var(--muted)", fontSize: '0.9375rem', cursor: "pointer", width: isMobile ? "100%" : "auto" }}
                          >
                            <option value="">Selecionar motorista</option>
                            {motoristasDisponiveis.map(m => <option key={m.id} value={m.id} disabled={usedMotoristas.includes(m.id) && g.motorista?.id !== m.id}>{m.nome}{usedMotoristas.includes(m.id) && g.motorista?.id !== m.id ? " (em uso)" : ""}</option>)}
                          </select>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {g.pessoas.map((p) => {
                          const passengerKey = p.programacao_passageiro_id || p.id;
                          return (
                            <div
                              key={passengerKey}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                background: "var(--s3)",
                                borderRadius: 6,
                                padding: isPlanningPrograms && grupos.length > 1 ? "4px 8px" : "3px 10px",
                                fontSize: '0.875rem',
                                color: "var(--muted)",
                                flexWrap: "wrap",
                              }}
                            >
                              <span>{p.nome}</span>
                              {p.id_pedido_origem && (
                                <span style={{ color: "#93c5fd", fontSize: '0.8125rem' }}>
                                  origem: {p.id_pedido_origem}
                                </span>
                              )}
                              {isPlanningPrograms && grupos.length > 1 && (
                                <select
                                  value={i}
                                  onChange={(event) => moveProgramPassenger(i, passengerKey, Number(event.target.value))}
                                  title="Mover passageiro para outra van"
                                  style={{
                                    background: "var(--s2)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 6,
                                    padding: "2px 6px",
                                    color: "var(--text)",
                                    fontSize: '0.8125rem',
                                    cursor: "pointer",
                                    maxWidth: isMobile ? "100%" : 130,
                                  }}
                                >
                                  {grupos.map((targetGroup, targetIndex) => (
                                    <option key={targetIndex} value={targetIndex}>
                                      Van {targetIndex + 1} · PG {targetGroup.programacao_codigo_responsavel || "—"}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  );
                })}
              </div>
              {isPlanningPrograms && !allAssigned && (
                <div
                  style={{ background: "rgba(59,130,246,0.09)", border: "1px solid rgba(59,130,246,0.3)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "var(--muted)", fontSize: '0.9375rem', lineHeight: 1.55 }}
                >
                  Você pode salvar estas vans para completar depois. Motorista e veículo são obrigatórios somente nas vans que serão enviadas.
                  {" "}Pendente em: {pendingExecutorVans.map((item) => `van ${item.index + 1}${!item.motorista && !item.veiculo ? " (motorista e veículo)" : !item.motorista ? " (motorista)" : " (veículo)"}`).join(", ")}.
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <Btn variant="secondary" onClick={() => {
                  if (isPlanningPrograms) setGruposRegiao(buildDraftRegionGroups(grupos));
                  setStep(2);
                }}><Icon name="back" size={16} /> Voltar</Btn>
                <Btn
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    if (isPlanningPrograms) { salvarNoBacklog(); return; }
                    setConfirmAction("backlog"); setIsConfirmModalOpen(true);
                  }}
                  disabled={loading || !grupos.length}
                  style={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                  }}
                >
                  {loading && pendingAction === "backlog"
                    ? <><Spinner /> {isPlanningPrograms ? "Salvando..." : "Salvando..."}</>
                    : (isPlanningPrograms
                      ? "Salvar planejamento"
                      : (isEditingDraft ? "Atualizar planejamento" : "Salvar planejamento"))}
                </Btn>
                {isPlanningPrograms && <>
                  <Btn variant="secondary" disabled={markedGroups.size < 2 || loading} onClick={joinMarkedGroups}>Juntar em uma van ({markedGroups.size})</Btn>
                  <Btn disabled={!canReviewPlanning || loading} onClick={reviewPlanning}>Revisar e enviar ({dispatchIndexes.length})</Btn>
                  {lastPlanningDispatchId && <Btn variant="secondary" onClick={() => onPlanningComplete?.({ routeId: lastPlanningDispatchId })}>Acompanhar envio</Btn>}
                  {embedded && <Btn variant="secondary" onClick={() => onPlanningComplete?.()}>Voltar aos pedidos</Btn>}
                </>}
                {!isPlanningPrograms && (
                <Btn
                  size="lg"
                  onClick={() => { setConfirmAction("gerar"); setIsConfirmModalOpen(true); }}
                  style={{ opacity: (!allAssigned || loading) ? 0.4 : 1, pointerEvents: (!allAssigned || loading) ? "none" : "auto" }}
                >
                  {loading && pendingAction === "gerar" ? <><Spinner /> Enviando...</> : (isEditingDraft ? <><Icon name="route" /> Atualizar e gerar rotas</> : <><Icon name="route" /> Gerar rotas</>)}
                </Btn>
                )}
              </div>
            </>
          ))}

          {/* Step 4 */}
          {step === 4 && (
            <>
              <div style={{ background: routeOutcome?.mode === "rascunho" ? "rgba(59,130,246,0.08)" : "rgba(34,197,94,0.06)", border: routeOutcome?.mode === "rascunho" ? "1px solid rgba(59,130,246,0.2)" : "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "14px 18px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
                <Icon name={routeOutcome?.mode === "rascunho" ? "document" : "send"} size={24} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 2 }}>
                    {routeOutcome?.mode === "rascunho"
                      ? (routeOutcome?.isProgramPlanning
                        ? (routeOutcome?.isTestRoute ? "Planejamento de teste salvo!" : "Planejamento salvo!")
                        : (routeOutcome?.wasEditing
                          ? (routeOutcome?.isTestRoute ? "Planejamento de teste atualizado!" : "Planejamento atualizado!")
                          : (routeOutcome?.isTestRoute ? "Planejamento de teste salvo!" : "Planejamento salvo!")))
                      : (routeOutcome?.wasEditing ? "Rota atualizada e enviada." : "Rotas enviadas.")}
                  </div>
                  <div style={{ fontSize: '0.9375rem', color: "var(--muted)" }}>
                    {routeOutcome?.mode === "rascunho"
                      ? (routeOutcome?.isProgramPlanning
                        ? "Volte à aba Planejamento em Pedidos para confirmar e enviar aos motoristas."
                        : (routeOutcome?.wasEditing
                          ? "As alterações foram salvas. Você ainda pode voltar e ajustar novamente antes da confirmação."
                          : "Nenhum documento foi criado e nenhuma mensagem foi disparada. Confirme depois em Pedidos."))
                      : `Cada motorista receberá uma mensagem no WhatsApp para confirmar o ponto de ${trecho === "ida" ? "partida" : "chegada"}.`}
                    {routeOutcome?.mode === "enviada" && routeOutcome?.isTestRoute && <> Continua marcado como <strong>teste</strong> para exclusão posterior pelo superadmin.</>}
                    {dataFormatada && <> Rota agendada para <strong>{dataFormatada}</strong> às <strong>{horario}</strong>.</>}
                  </div>
                  {routeOutcome?.preservedExternal > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9375rem', color: "#fbbf24", lineHeight: 1.45 }}>
                      <Icon name="warning" /> {routeOutcome.preservedExternal} mensagem(ns) do disparo anterior permanece(m) no histórico da Meta e não pode(m) ser apagada(s). Oriente o motorista a usar apenas a mensagem corrigida mais recente.
                    </div>
                  )}
                  {routeOutcome?.expiredPreviousRoutes?.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '0.9375rem', color: "#fbbf24", lineHeight: 1.45 }}>
                      <Icon name="warning" /> {routeOutcome.expiredPreviousRoutes.length} solicitação(ões) anterior(es) deste motorista
                      expiraram sem resposta e foram encerradas. Consulte o histórico das rotas anteriores para
                      verificar os passageiros que ficaram sem link.
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: "grid", gap: 16, marginBottom: 24 }}>
                {grupos.map((g, i) => {
                  const s = REGIAO_COLORS[g.regiao] || REGIAO_COLORS["Sem região"];
                  return (
                    <Card key={i}>
                      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                        <div style={{ width: 36, height: 36, background: s.bg, border: `1px solid ${s.color}40`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: '1rem' }}><Icon name="fleet" /></div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{g.regiao} — {g.motorista?.nome}</div>
                          <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>
                            {g.pessoas.length} passageiro{g.pessoas.length !== 1 ? "s" : ""} · {g.veiculo?.veiculo} ({g.veiculo?.placa}) · {trecho === "ida" ? "🏭 Ida" : "🏠 Volta"}
                          </div>
                        </div>
                        <div style={{ marginLeft: isMobile ? 0 : "auto", display: "flex", alignItems: "center", gap: 6, background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.2)", borderRadius: 8, padding: "4px 10px" }}>
                          <span style={{ fontSize: '0.875rem' }}>⏳</span>
                          <span style={{ fontSize: '0.875rem', color: "#eab308", fontWeight: 600 }}>Aguardando motorista</span>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Passageiros</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {g.pessoas.map(p => (
                          <div key={p.programacao_passageiro_id || p.id} style={{ background: "var(--s2)", borderRadius: 8, padding: "6px 10px", fontSize: '0.875rem' }}>
                            <div style={{ fontWeight: 500 }}>{p.nome}</div>
                            <div style={{ color: "var(--muted)", fontSize: '0.8125rem', marginTop: 2 }}>{p.rua}, {p.numero} — {p.bairro}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                {embedded && isPlanningPrograms ? (
                  <Btn variant="secondary" onClick={() => onPlanningComplete?.()}><Icon name="back" size={16} /> Voltar aos pedidos</Btn>
                ) : (
                  <Btn variant="secondary" onClick={reset}>↩ Criar Nova Rota</Btn>
                )}
              </div>
            </>
          )}
        </>
      )}

      {leaveAction && <Modal title="Alterações não salvas" onClose={() => !loading && setLeaveAction(null)}>
        <p style={{ color: 'var(--muted)', lineHeight: 1.6, marginBottom: 18 }}>A montagem foi alterada. Salve antes de sair para continuar de onde parou.</p>
        <div className="planning-actions">
          <Btn variant="secondary" disabled={loading} onClick={() => setLeaveAction(null)}>Continuar editando</Btn>
          <Btn variant="danger" disabled={loading} onClick={() => { const action = leaveAction.run; setLeaveAction(null); action(); }}>Sair sem salvar</Btn>
          <Btn disabled={loading} onClick={saveAndLeave}>{loading ? 'Salvando…' : 'Salvar e sair'}</Btn>
        </div>
      </Modal>}
      {correctionRoute && <PlanningVanCorrection route={correctionRoute} drivers={motoristasDisponiveis} vehicles={veiculosDisponiveis}
        reservedDrivers={[...grupos.map(g => Number(g.motorista?.id)), ...(programRoute?.vans_enviadas || []).filter(v => v.id !== correctionRoute.id).map(v => Number(v.motorista?.id))]}
        reservedVehicles={[...grupos.map(g => Number(g.veiculo?.id)), ...(programRoute?.vans_enviadas || []).filter(v => v.id !== correctionRoute.id).map(v => Number(v.veiculo?.id))]}
        loading={loading} onClose={() => setCorrectionRoute(null)} onSave={savePlanningCorrection} />}
      {planningReview && <PlanningDispatchModal route={planningReview.route} initialGroupIds={planningReview.groupIds} remainingGroups={planningReview.retryRouteId ? grupos : undefined} progress={planningProgress} destination={getDestinoLabel(routeConfig)}
        loading={loading} onClose={() => setPlanningReview(null)} onConfirm={sendSelectedPlanning} />}
      {isConfirmModalOpen && (
        <Modal title={confirmAction === "backlog"
          ? (isPlanningPrograms ? "Salvar planejamento" : (isEditingDraft ? "Atualizar planejamento" : "Salvar planejamento"))
          : (isEditingDraft ? "Confirmar atualização e geração" : "Confirmar geração de rota")} onClose={() => { setIsConfirmModalOpen(false); setConfirmAction(""); }}>
          <div style={{ display: "grid", gap: 20 }}>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "var(--s2)", borderRadius: 10, border: "1px solid var(--border)" }}>
                <span style={{ fontSize: '1.25rem' }}><Icon name="history" /></span>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Data</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', marginTop: 2 }}>{dataFormatada || data} - {horario}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "var(--s2)", borderRadius: 10, border: "1px solid var(--border)" }}>
                <span style={{ fontSize: '1.25rem' }}>{trecho === "ida" ? "🏭" : "🏠"}</span>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Trecho</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', marginTop: 2 }}>{rotaLabel}</div>
                </div>
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                <p style={{ fontWeight: 900, color: "white" }}>
                  {confirmAction === "backlog"
                    ? "Revise os detalhes antes de salvar. Esta ação apenas persiste o planejamento; nenhuma mensagem será enviada agora. O envio será confirmado posteriormente na tela de Pedidos."
                    : "Revise os detalhes antes de enviar. As mensagens serão enfileiradas e a rota será confirmada somente após o canal registrar o envio."}
                </p>

                {editingDraft?.hadDispatch && (
                  <div style={{ color: "#fbbf24", fontSize: '0.9375rem', lineHeight: 1.55 }}>
                    O disparo anterior será encerrado no RotaFlex. Se a Meta já aceitou a mensagem, ela continuará no WhatsApp e no histórico de auditoria. Confirme a correção novamente e avise o motorista para considerar somente a mensagem mais recente.
                  </div>
                )}

                {grupos.map((group, index) => (
                  <Card key={`${group.regiao}-${index}`} style={{ padding: 16 }}>
                    <div style={{ fontSize: '0.9375rem', color: "white", fontWeight: 700, marginTop: 1 }}>
                      {group.programacao_codigo_responsavel
                        ? `Van responsável pelo pedido ${group.programacao_codigo_responsavel} · `
                        : ""}
                      Motorista: {group.motorista?.nome || "—"} · Veículo:{" "}
                      {group.veiculo ? `${group.veiculo.veiculo} (${group.veiculo.placa})` : "—"}
                    </div>

                    <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                      {(group.pessoas || []).map((person) => (
                        <div
                          key={person.programacao_passageiro_id || person.id || `${person.nome}-${person.telefone}`}
                          style={{ background: "var(--s3)", borderRadius: 8, padding: "7px 10px", fontSize: '0.9375rem', color: "white" }}
                        >
                          {person.nome} - {person.telefone || "sem telefone"}
                          {person.id_pedido_origem && (
                            <div style={{ color: "#93c5fd", fontSize: '0.8125rem', marginTop: 3 }}>
                              Pedido de origem: {person.id_pedido_origem}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </Card>
                ))}
              </div>
            </div>


            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <Btn variant="secondary" onClick={() => { setIsConfirmModalOpen(false); setConfirmAction(""); }}><Icon name="back" size={16} /> Editar</Btn>
              {confirmAction === "gerar" && (
                <Btn
                  variant="secondary"
                  onClick={() => {
                    setIsConfirmModalOpen(false);
                    setConfirmAction("");
                    salvarNoBacklog();
                  }}
                >
                  {isEditingDraft ? "Atualizar planejamento" : "Salvar planejamento"}
                </Btn>
              )}
              <Btn onClick={() => {
                setIsConfirmModalOpen(false);
                const action = confirmAction;
                setConfirmAction("");
                if (action === "backlog") {
                  salvarNoBacklog();
                  return;
                }
                gerarRotas();
              }}>{confirmAction === "backlog"
                ? (isPlanningPrograms ? "Salvar planejamento" : (isEditingDraft ? "Salvar alterações" : "Salvar planejamento"))
                : (isEditingDraft ? "Atualizar e gerar rotas" : "Confirmar e gerar rotas")}</Btn>
            </div>
          </div>
        </Modal>
      )}

      {isAgruparModalOpen && (
        <Modal title={`Agrupar ${groupingPluralLabel}`} onClose={fecharModalAgrupar}>
          <div style={{ display: "grid", gap: 16 }}>
            <p style={{ color: "var(--muted)", fontSize: '1rem', lineHeight: 1.5 }}>
              Selecione duas ou mais regiões para planejar seus passageiros em conjunto.
            </p>

            <Input
              label="Nome do agrupamento"
              value={nomeAgrupamento}
              onChange={(e) => setNomeAgrupamento(e.target.value)}
              placeholder="Ex: Vale do Sol + Centro"
            />

            <div style={{ display: "grid", gap: 10, maxHeight: 320, overflowY: "auto" }}>
              {gruposRegiao.map((gr) => {
                const checked = gruposSelecionadosParaAgrupar.includes(gr.id);
                return (
                  <label
                    key={gr.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: "1px solid var(--border)",
                      background: checked ? "rgba(249,115,22,0.08)" : "var(--s2)",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGrupoParaAgrupar(gr.id)}
                      style={{ marginTop: 2, accentColor: "var(--accent)", width: 16, height: 16 }}
                    />
                    <div>
                      <div style={{ fontWeight: 600 }}>{gr.regiao}</div>
                      <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 4 }}>
                        {gr.pessoas.length} pessoa{gr.pessoas.length !== 1 ? "s" : ""} · {gr.qtdVeiculos} veículo{gr.qtdVeiculos !== 1 ? "s" : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <Btn variant="secondary" onClick={fecharModalAgrupar}>Cancelar</Btn>
              <Btn
                onClick={confirmarAgrupamentoRegioes}
                style={{ opacity: canConfirmarAgrupamento ? 1 : 0.5 }}
              >
                Confirmar Agrupamento
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(phoneValidationDialog)}
        title="Telefones com problema no WhatsApp"
        message={(
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Alguns telefones não passaram na validação. O planejamento continua salvo para nova tentativa.
              Deseja enviar mesmo assim?
            </p>
            <div style={{ display: "grid", gap: 8 }}>
              {formatPhoneValidationFalhas(phoneValidationDialog?.falhas || []).map((linha) => (
                <div
                  key={linha}
                  style={{
                    fontSize: '0.9375rem',
                    color: "#fca5a5",
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    lineHeight: 1.45,
                  }}
                >
                  {linha}
                </div>
              ))}
            </div>
          </div>
        )}
        cancelLabel="Não"
        confirmLabel="Sim, enviar mesmo assim"
        confirmVariant="primary"
        loading={loading && pendingAction === "gerar"}
        onClose={() => {
          if (loading && pendingAction === "gerar") return;
          setPhoneValidationDialog(null);
        }}
        onConfirm={confirmDispatchIgnoringPhoneValidation}
      />

      <ConfirmModal
        open={Boolean(sgtValidationDialog)}
        title={sgtValidationDialog?.sgt?.code === "DIVERGENCIA_SGT"
          ? "Divergência com o portal SGT (Embraer)"
          : "Não foi possível conferir com o SGT"}
        message={(
          <div style={{ display: "grid", gap: 12 }}>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              {sgtValidationDialog?.sgt?.code === "DIVERGENCIA_SGT"
                ? <>O {sgtValidationDialog?.sgt?.grupoLabel || "grupo"} está diferente do pedido no SGT. Revise antes de enviar ou continue mesmo assim.</>
                : (SGT_MOTIVO_MENSAGENS[sgtValidationDialog?.sgt?.motivo] || "A conferência com o SGT não pôde ser feita.")}
            </p>
            {formatSgtSnapshotLine(sgtValidationDialog?.sgt?.snapshot) && (
              <p style={{ margin: 0, fontSize: '0.9375rem', opacity: 0.8 }}>
                {formatSgtSnapshotLine(sgtValidationDialog?.sgt?.snapshot)}
              </p>
            )}
            {sgtValidationDialog?.sgt?.ultimaTentativa?.status === "error" && (
              <p style={{ margin: 0, fontSize: '0.9375rem', color: "#fca5a5" }}>
                A última tentativa de busca no SGT falhou{sgtValidationDialog.sgt.ultimaTentativa.erro_mensagem ? `: ${sgtValidationDialog.sgt.ultimaTentativa.erro_mensagem}` : "."}
              </p>
            )}
            <div style={{ display: "grid", gap: 8 }}>
              {formatSgtConferencias(sgtValidationDialog?.sgt?.conferencias || []).map((item) => (
                <div
                  key={item.id_pedido}
                  style={{
                    fontSize: '0.9375rem',
                    color: "#fca5a5",
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    lineHeight: 1.45,
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>Pedido {item.id_pedido}</div>
                  {item.linhas.map((linha) => (
                    <div key={linha}>{linha}</div>
                  ))}
                </div>
              ))}
            </div>
            <label style={{ display: "grid", gap: 4, fontSize: '0.9375rem' }}>
              <span style={{ opacity: 0.85 }}>Justificativa para continuar mesmo assim (obrigatória — fica registrada na auditoria):</span>
              <textarea
                value={sgtOverrideMotivo}
                onChange={(e) => setSgtOverrideMotivo(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Ex.: pedido ajustado por telefone com a central"
                style={{
                  resize: "vertical",
                  borderRadius: 8,
                  border: "1px solid rgba(148,163,184,0.4)",
                  background: "transparent",
                  color: "inherit",
                  padding: "8px 10px",
                  fontSize: '0.9375rem',
                }}
              />
            </label>
            <p style={{ margin: 0, lineHeight: 1.6, fontSize: '0.9375rem', opacity: 0.85 }}>
              O planejamento continua salvo — ao escolher Corrigir, o disparo é interrompido.
            </p>
          </div>
        )}
        cancelLabel="Corrigir"
        confirmLabel="Continuar mesmo assim"
        confirmVariant="primary"
        loading={loading && pendingAction === "gerar"}
        confirmDisabled={!sgtOverrideMotivo.trim()}
        onClose={() => {
          if (loading && pendingAction === "gerar") return;
          setSgtValidationDialog(null);
        }}
        onConfirm={confirmDispatchIgnoringSgtValidation}
      />

      {passengerEditor && <PlanningPassengersModal group={passengerEditor.group} programs={planningPrograms} candidates={planningPassengerCandidates}
        blockedIds={new Set([...grupos.filter(group => group.planejamento_grupo_id !== passengerEditor.group.planejamento_grupo_id).flatMap(group => group.pessoas),
          ...(programRoute?.vans_enviadas || []).flatMap(van => van.grupo?.pessoas || [])].map(person => Number(person.id)))}
        busy={loading} onSave={savePlanningPassengers} onClose={() => !loading && setPassengerEditor(null)} />}
      <ConfirmModal open={Boolean(passengerEditGate)} title="Salvar planejamento antes de editar passageiros?"
        message="As alterações na distribuição, no motorista e no veículo precisam ser salvas antes de incluir ou retirar passageiros."
        confirmLabel="Salvar e continuar" loading={loading} onConfirm={saveAndOpenPassengerEditor} onClose={() => !loading && setPassengerEditGate(null)} />
      {toast && !(isEditingDraft && editConflict) && (
        <Toast onDismiss={() => setToast("")} tone={toastType === "error" ? "error" : "success"} style={{ bottom: isMobile ? 24 : 104 }}>{toast}</Toast>
      )}
    </div>
  );
}

MontarRotaPage.propTypes = {
  pessoas: PropTypes.array.isRequired, motoristas: PropTypes.array.isRequired, veiculos: PropTypes.array.isRequired,
  draftRoute: PropTypes.object, onClearDraftRoute: PropTypes.func, programPlanningContext: PropTypes.object,
  onEditProgram: PropTypes.func, onUndoPlanning: PropTypes.func,
  onClearProgramPlanning: PropTypes.func, embedded: PropTypes.bool, onBack: PropTypes.func, onPlanningComplete: PropTypes.func,
};
